import coffeeLottieUrl from "@/assets/Coffee love.lottie?url";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ChartContainer, type ChartConfig } from "@/components/ui/chart";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AjukanPertanyaanForm } from "./partials/AjukanPertanyaanForm";
import { RiwayatPertanyaanDialog } from "./partials/RiwayatPertanyaanDialog";
import { RemunInfoFooter } from "./partials/RemunInfoFooter";
import { QnaRemunerasiDialog } from "./partials/QnaRemunerasiDialog";

import { TooltipProvider } from "@/components/ui/tooltip";
import { useMasterPeriode } from "@/hooks/use-master-periode";
import { usePeriodeStore } from "@/hooks/use-periode";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowUpRight,
  Briefcase,
  Calendar,
  CheckCircle2,
  ChevronDown,
  Clock,
  Download,
  Eye,
  EyeOff,
  FileText,
  Info,
  List,
  Loader2,
  MessageSquare,
  RefreshCw,
  ShieldCheck,
  Target,
  User,
  Wallet,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import {
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  RadialBar,
  RadialBarChart,
  Label as RechartsLabel,
} from "recharts";
import { toast } from "sonner";
import { writeFile } from "xlsx";
import { fetchAgendaMengajar } from "./api/get-agenda-mengajar";
import { getAkumulasiIku } from "./api/get-akumulasi-iku";
import { getConfigPersentase } from "./api/get-config-persentase";
import { fetchDosenRemunDetail } from "./api/get-dosen-remun-detail";
import { getKonstantaFungsional } from "./api/get-konstanta-fungsional";
import { fetchMasterPeriodeDetail } from "./api/get-master-periode-detail";
import { fetchUnsurJabatanTambahan } from "./api/get-unsur-jabatan-tambahan";
import { fetchUnsurMembimbingSeminarProposal } from "./api/get-unsur-membimbing-seminar-proposal";
import { fetchUnsurMembimbingTa } from "./api/get-unsur-membimbing-ta";
import { fetchUnsurMengujiProposal } from "./api/get-unsur-menguji-proposal";
import { fetchUnsurMengujiTa } from "./api/get-unsur-menguji-ta";
import { fetchUnsurPelaksanaanPendidikan } from "./api/get-unsur-pelaksanaan-pendidikan";
import { fetchUnsurPembimbingAkademik } from "./api/get-unsur-pembimbing-akademik";
import { fetchUnsurPenelitian } from "./api/get-unsur-penelitian";
import { fetchUnsurPenunjang } from "./api/get-unsur-penunjang";
import { getValidationStatus } from "./api/get-validation-status";
import { getMasterKonstanta } from "./api/master-konstanta";
import { getExport70New } from "./api/get-export-70-new";
import { postSyncKinerja } from "./api/post-sync-kinerja";
import { postValidationStatus } from "./api/post-validation-status";
import { AgendaSheet } from "./partials/AgendaSheet";
// import { AutomatedIkuClaimer } from "./partials/AutomatedIkuClaimer";
import { RincianPerBulan } from "./partials/RincianPerBulan";
import { TabelIku } from "./partials/TabelIku";
import { TabelPeformaKinerja } from "./partials/TabelPeformaKinerja";
import { TabelValidasiKinerja } from "./partials/TabelValidasiKinerja";
import { updateIkuConfig } from "./utils/iku-calculations";
import { getIku6 } from "./api/get-iku-penelitian";
import { calculateCapaianKinerja, createRemunDetailWorkbook, type MonthlyExportData } from "./utils/export-remun-detail";

const formatRupiah = (angka: number | null | undefined) => {
  if (angka === null || angka === undefined) return "Rp 0";
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
  }).format(angka);
};

import { ProtectRole } from "@/components/ProtectRole";
import { Loader } from "@/components/ui/loader";
import { ROLES } from "@/config/roles";
import { useAuth } from "@/hooks/use-auth";
import { roundNumber } from "@/lib/utils";

const sksChartConfig = {
  sks: {
    label: "SKS",
    color: "hsl(var(--chart-2))",
  },
} satisfies ChartConfig;

export const WHITELIST_NIU = [
  // "202500028",
  "201701553",
  "201701140",
  "201701300",
  "201701195",
  "201701350",
];

export default function RemunDosenDetailPage() {
  const { niu: urlNiu } = useParams();
  const { user, isAuthenticated } = useAuth();
  const niu = user?.niu || urlNiu;

  const navigate = useNavigate();
  const { getYearAndSemester } = usePeriodeStore();
  const { year, semester } = getYearAndSemester();
  const periodeId = 65;

  const queryClient = useQueryClient();
  const { data: masterPeriodes } = useMasterPeriode({
    enabled: isAuthenticated,
  });
  const currentPeriod = masterPeriodes?.find(
    (p: any) =>
      p.tahun.toString() === year.toString() &&
      p.semester.toString() === semester.toString(),
  );
  const pir = currentPeriod?.pir || 4490;

  const { data: configPersentase } = useQuery({
    queryKey: ["config-persentase"],
    queryFn: getConfigPersentase,
    enabled: isAuthenticated,
  });

  useEffect(() => {
    if (configPersentase?.persentase_iku !== undefined) {
      updateIkuConfig(configPersentase.persentase_iku);
    }
  }, [configPersentase?.persentase_iku]);

  const [openSyncConfirm, setOpenSyncConfirm] = useState(false);
  const [isExportingExcel, setIsExportingExcel] = useState(false);
  const [openSyncLoading, setOpenSyncLoading] = useState(false);

  const syncMutation = useMutation({
    mutationFn: postSyncKinerja,
    onMutate: () => {
      setOpenSyncConfirm(false);
      setOpenSyncLoading(true);
      toast.loading("Menarik data eviden...", {
        id: "sync-kinerja-detail",
        position: "bottom-right",
      });
    },
    onSuccess: (resData) => {
      setOpenSyncLoading(false);
      toast.success(
        resData.message ||
          "Proses penarikan data evidence telah selesai dieksekusi",
        {
          id: "sync-kinerja-detail",
        },
      );
      // Optionally invalidate queries
      queryClient.invalidateQueries({ queryKey: ["dosen-remun-detail"] });
    },
    onError: (error: any) => {
      setOpenSyncLoading(false);
      toast.error(error?.message || "Gagal menarik data eviden");
    },
  });

  const validationStatusMutation = useMutation({
    mutationFn: postValidationStatus,
    onSuccess: async () => {
      toast.loading("Memperbarui status...", { id: "update-validasi" });

      // tunggu SEMUA invalidate + refetch benar-benar selesai
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["dosen-remun-detail"] }),
        queryClient.invalidateQueries({ queryKey: ["validation-status"] }),
        queryClient.invalidateQueries({
          queryKey: ["unsur-pelaksanaan-pendidikan"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["unsur-membimbing-seminar-proposal"],
        }),
        queryClient.invalidateQueries({ queryKey: ["unsur-menguji-proposal"] }),
        queryClient.invalidateQueries({ queryKey: ["unsur-membimbing-ta"] }),
        queryClient.invalidateQueries({ queryKey: ["unsur-menguji-ta"] }),
        queryClient.invalidateQueries({ queryKey: ["unsur-penunjang"] }),
        queryClient.invalidateQueries({ queryKey: ["unsur-jabatan-tambahan"] }),
        queryClient.invalidateQueries({ queryKey: ["unsur-penelitian"] }),
        queryClient.invalidateQueries({
          queryKey: ["unsur-pembimbing-akademik"],
        }),
      ]);

      toast.success("Data berhasil dikonfirmasi!", { id: "update-validasi" });
      setShowKonfirmasiPopup(false);
      setIsKonfirmasiChecked(false);
    },
    onError: (error: any) => {
      toast.error(error?.message || "Gagal mengkonfirmasi data");
    },
  });

  const [agendaSheetOpen, setAgendaSheetOpen] = useState(false);
  const [selectedMonthFilter, setSelectedMonthFilter] = useState<string | null>(
    null,
  );
  const [selectedAgendaParams, setSelectedAgendaParams] = useState<{
    nip: string;
    kode_matkul: string;
    tahun: string | number;
    semester: string | number;
    kelas: string;
    nama_matkul: string;
  } | null>(null);

  const isAdmin = user?.role === ROLES.ADMIN_SUPER;
  const isAdminUniv = user?.role === ROLES.ADMIN_UNIV;
  const isDosen = user?.role === ROLES.DOSEN;
  // const isNiuKhusus = WHITELIST_NIU.includes(niu as string);

  // const [showRupiah, setShowRupiah] = useState(
  //   isAdmin || isAdminUniv || isNiuKhusus,
  // );
  // const [showNilai, setShowNilai] = useState(
  //   isAdmin || isAdminUniv || isNiuKhusus,
  // );
  const [showRupiah, setShowRupiah] = useState(true);
  const [showNilai, setShowNilai] = useState(true);
  const [showValidasi, setShowValidasi] = useState(false);
  const [showRincian, setShowRincian] = useState(showNilai ? true : false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showDosenPopup, setShowDosenPopup] = useState(false);
  const [showKonfirmasiPopup, setShowKonfirmasiPopup] = useState(false);
  const [isKonfirmasiChecked, setIsKonfirmasiChecked] = useState(false);

  const [openPenelitian, setOpenPenelitian] = useState(false);
  const [openPengabdian, setOpenPengabdian] = useState(false);
  const [openPesan, setOpenPesan] = useState(false);
  const [openRiwayatPesan, setOpenRiwayatPesan] = useState(false);

  const [isTimelineOpen, setIsTimelineOpen] = useState(() => {
    const saved = localStorage.getItem("remun-timeline-open");
    return saved === "true";
  });

  const handleTimelineOpenChange = (open: boolean) => {
    setIsTimelineOpen(open);
    localStorage.setItem("remun-timeline-open", open.toString());
  };

  const scrollToUnsur = (index: number) => {
    const el = document.getElementById(`unsur-${index}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    setIsMenuOpen(false);
  };

  const { data: agendaData, isLoading: isAgendaLoading } = useQuery({
    queryKey: ["agenda-mengajar", selectedAgendaParams],
    queryFn: () =>
      selectedAgendaParams
        ? fetchAgendaMengajar(selectedAgendaParams)
        : Promise.resolve([]),
    enabled: !!selectedAgendaParams && agendaSheetOpen && isAuthenticated,
  });

  const validationStatusQuery = useQuery({
    queryKey: ["validation-status", niu, year, semester],
    queryFn: () => getValidationStatus(niu as string, year, semester),
    enabled: !!niu && !!year && !!semester,
  });

  const isAlreadyConfirmed =
    validationStatusQuery.data?.data?.overall_completed === true;

  const { data, isPending, isError, error } = useQuery({
    queryKey: ["dosen-remun-detail", niu, year, semester, periodeId],
    queryFn: () =>
      fetchDosenRemunDetail({
        niu: niu as string,
        year,
        semester,
        periodeID: periodeId,
      }),
    enabled: !!niu && !!year && !!semester && !!periodeId && isAuthenticated,
  });

  const mappedMonthsTemp = Object.keys(data?.data_per_bulan || {});
  const nowTemp = new Date();
  const currentMonthKeyTemp = `${nowTemp.getFullYear()}_${String(nowTemp.getMonth() + 1).padStart(2, "0")}`;
  const initialMonthTemp = mappedMonthsTemp.includes(currentMonthKeyTemp)
    ? currentMonthKeyTemp
    : mappedMonthsTemp[mappedMonthsTemp.length - 1];
  const activeMonthFilterTemp = selectedMonthFilter || initialMonthTemp;

  const { data: konstantaFungsionalData } = useQuery({
    queryKey: [
      "konstanta-fungsional",
      niu,
      year,
      semester,
      periodeId,
      activeMonthFilterTemp,
    ],
    queryFn: () =>
      getKonstantaFungsional({
        niu: niu as string,
        year: year as string | number,
        semester: semester as string | number,
        periodeID: periodeId as string | number,
        month: activeMonthFilterTemp,
      }),
    enabled:
      !!niu &&
      !!year &&
      !!semester &&
      !!periodeId &&
      !!activeMonthFilterTemp &&
      isAuthenticated,
  });

  const { data: export70Data } = useQuery({
    queryKey: ["export-70-new", niu, year, semester, periodeId],
    queryFn: () =>
      getExport70New({
        niu: niu as string,
        year: year as string | number,
        semester: semester as string | number,
        periodeID: periodeId as string | number,
      }),
    enabled: !!niu && !!year && !!semester && !!periodeId && isAuthenticated,
  });

  const { data: masterKonstantaData } = useQuery({
    queryKey: ["master-konstanta", year, semester],
    queryFn: () =>
      getMasterKonstanta(year as string | number, semester as string | number),
    enabled: !!year && !!semester,
  });

  const { data: masterPeriodeDetail } = useQuery({
    queryKey: ["master-periode-detail", periodeId],
    queryFn: () => fetchMasterPeriodeDetail(String(periodeId)),
    enabled: !!periodeId && isAuthenticated,
  });

  const { data: unsurPendidikanData, isLoading: isUnsurLoading } = useQuery({
    queryKey: ["unsur-pelaksanaan-pendidikan", niu, year, semester, periodeId],
    queryFn: () =>
      fetchUnsurPelaksanaanPendidikan({
        niu: niu as string,
        year,
        semester,
        periodeID: periodeId,
      }),
    enabled: !!niu && !!year && !!semester && !!periodeId && isAuthenticated,
  });

  const { data: unsurSeminarProposalData, isLoading: isSeminarLoading } =
    useQuery({
      queryKey: [
        "unsur-membimbing-seminar-proposal",
        niu,
        year,
        semester,
        periodeId,
      ],
      queryFn: () =>
        fetchUnsurMembimbingSeminarProposal({
          niu: niu as string,
          year,
          semester,
          periodeID: periodeId,
        }),
      enabled: !!niu && !!year && !!semester && !!periodeId && isAuthenticated,
    });

  const {
    data: unsurMengujiProposalData,
    isLoading: isMengujiProposalLoading,
  } = useQuery({
    queryKey: ["unsur-menguji-proposal", niu, year, semester, periodeId],
    queryFn: () =>
      fetchUnsurMengujiProposal({
        niu: niu as string,
        year,
        semester,
        periodeID: periodeId,
      }),
    enabled: !!niu && !!year && !!semester && !!periodeId && isAuthenticated,
  });

  const { data: unsurMembimbingTaData, isLoading: isMembimbingTaLoading } =
    useQuery({
      queryKey: ["unsur-membimbing-ta", niu, year, semester, periodeId],
      queryFn: () =>
        fetchUnsurMembimbingTa({
          niu: niu as string,
          year,
          semester,
          periodeID: periodeId,
        }),
      enabled: !!niu && !!year && !!semester && !!periodeId && isAuthenticated,
    });

  const { data: unsurMengujiTaData, isLoading: isMengujiTaLoading } = useQuery({
    queryKey: ["unsur-menguji-ta", niu, year, semester, periodeId],
    queryFn: () =>
      fetchUnsurMengujiTa({
        niu: niu as string,
        year,
        semester,
        periodeID: periodeId,
      }),
    enabled: !!niu && !!year && !!semester && !!periodeId && isAuthenticated,
  });

  const { data: unsurPenunjangData, isLoading: isPenunjangLoading } = useQuery({
    queryKey: ["unsur-penunjang", niu, year, semester, periodeId],
    queryFn: () =>
      fetchUnsurPenunjang({
        niu: niu as string,
        year,
        semester,
        periodeID: periodeId,
      }),
    enabled: !!niu && !!year && !!semester && !!periodeId && isAuthenticated,
  });

  const {
    data: unsurJabatanTambahanData,
    isLoading: isJabatanTambahanLoading,
  } = useQuery({
    queryKey: ["unsur-jabatan-tambahan", niu, year, semester, periodeId],
    queryFn: () =>
      fetchUnsurJabatanTambahan({
        niu: niu as string,
        year,
        semester,
        periodeID: periodeId,
      }),
    enabled: !!niu && !!year && !!semester && !!periodeId && isAuthenticated,
  });

  const { data: unsurPenelitianData, isLoading: isPenelitianLoading } =
    useQuery({
      queryKey: ["unsur-penelitian", niu, year, semester, periodeId],
      queryFn: () =>
        fetchUnsurPenelitian({
          niu: niu as string,
          year,
          semester,
          periodeID: periodeId,
        }),
      enabled: !!niu && !!year && !!semester && !!periodeId && isAuthenticated,
    });

  const {
    data: unsurPembimbingAkademikData,
    isLoading: isPembimbingAkademikLoading,
  } = useQuery({
    queryKey: ["unsur-pembimbing-akademik", niu, year, semester, periodeId],
    queryFn: () =>
      fetchUnsurPembimbingAkademik({
        niu: niu as string,
        year,
        semester,
        periodeID: periodeId,
      }),
    enabled: !!niu && !!year && !!semester && !!periodeId && isAuthenticated,
  });

  const { data: akumulasiIkuResponse } = useQuery({
    queryKey: [
      "akumulasi-iku",
      niu,
      year,
      semester,
      periodeId,
      activeMonthFilterTemp,
    ],
    queryFn: () =>
      getAkumulasiIku(
        niu as string,
        year as string | number,
        semester as string | number,
        periodeId as string | number,
        activeMonthFilterTemp,
      ),
    enabled:
      !!niu &&
      !!year &&
      !!semester &&
      !!periodeId &&
      !!activeMonthFilterTemp &&
      isAuthenticated,
  });

  const totalPointDiperoleh = akumulasiIkuResponse?.datas?.total_point || 0;

  if (
    isPending ||
    isUnsurLoading ||
    isSeminarLoading ||
    isMengujiProposalLoading ||
    isMembimbingTaLoading ||
    isMengujiTaLoading ||
    isPenunjangLoading ||
    isJabatanTambahanLoading ||
    isPenelitianLoading ||
    isPembimbingAkademikLoading
  ) {
    return (
      <div className="container mx-auto py-8 flex items-center justify-center min-h-[80vh]">
        <Loader text="Memuat detail dosen..." className="" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="container mx-auto py-8 px-4">
        <Button variant="outline" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" /> Kembali
        </Button>
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6 text-red-600 font-medium">
            Error: {error ? (error as Error).message : "Data tidak ditemukan."}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Calculate totals
  const mappedMonths = Object.entries(data.data_per_bulan || {}).map(
    ([key, val]) => ({
      month: key,
      ...(val as any),
    }),
  );

  const activeMonthFilter = activeMonthFilterTemp;

  // Cari data sesuai filter bulan
  const currentMonthData: any =
    mappedMonths.find((m) => m.month === activeMonthFilter) || {};

  const statusPegawai = currentMonthData?.status_pegawai || "-";
  const statusSekarang = currentMonthData?.status_sekarang || "-";
  const cluster = currentMonthData?.cluster || "-";

  // Pre-compute map untuk judul komponen kegiatan dari masterPeriodeDetail
  const komponenTitleMap = new Map<string, string>();
  if (masterPeriodeDetail?.datas) {
    masterPeriodeDetail.datas.forEach((unsur: any) => {
      unsur.details?.forEach((d: any) => {
        if (d.detail?.kode_komponen) {
          const kode = d.detail.kode_komponen.toUpperCase();
          const kegiatan = d.komponen?.komponen_kegiatan || "";
          if (kegiatan) {
            komponenTitleMap.set(kode, `${kegiatan}`);
          }
        }
      });
    });
  }

  const combinedUnsurData = {
    ...(unsurPendidikanData || {}),
    ...(unsurSeminarProposalData || {}),
    ...(unsurMengujiProposalData || {}),
    ...(unsurMembimbingTaData || {}),
    ...(unsurMengujiTaData || {}),
    ...(unsurPembimbingAkademikData || {}),
    ...(unsurPenelitianData || {}),
    ...(unsurPenunjangData || {}),
  };

  // Ekstrak performa kinerja (ren_*)
  const performanceData = Object.keys(combinedUnsurData || {})
    .filter((key) => key.startsWith("ren_"))

    .map((key) => {
      const item = (combinedUnsurData as any)[key];

      const normalizedKode = key.replace("_", "").toUpperCase();
      const masterTitle = komponenTitleMap.get(normalizedKode);
      // Ganti title dari response dengan title dari master (jika ada)
      const title = masterTitle || item?.title || "-";

      const renKey = key.replace("ren_", "REN");
      const isJabatanTambahan =
        unsurJabatanTambahanData &&
        Object.prototype.hasOwnProperty.call(unsurJabatanTambahanData, renKey);

      let validationData: any[] = [];
      if ((combinedUnsurData as any)[`validate_${key}`]) {
        // dari unsurPendidikanData
        validationData = (combinedUnsurData as any)[`validate_${key}`];
      } else {
        const rawDataArray = (combinedUnsurData as any)[renKey];

        if (Array.isArray(rawDataArray)) {
          validationData = rawDataArray
            .map((item: any) => {
              const publikasiOrKarya =
                item.details_parsed?.publikasi || item.details_parsed?.karya;

              if (publikasiOrKarya && publikasiOrKarya.length > 0) {
                return publikasiOrKarya.map((pub: any) => ({
                  url:
                    item.details ||
                    pub?.static_path ||
                    pub.soft_copy ||
                    pub.url_publikasi ||
                    pub.url_atau_file ||
                    pub?.file_path,
                  url_file_path: pub?.file_path || item.details,
                  sks_judul:
                    pub.judul_artikel ||
                    pub.judul_karya ||
                    item.details_parsed?.no_sk ||
                    item.details?.split("$")[0] ||
                    "Karya Ilmiah",
                  is_iku: item.is_iku,
                  iku: item.iku,
                  details_parsed: {
                    ...pub,
                    validator_details:
                      item.details_parsed?.validator_details || [],
                    is_ketua: item.details_parsed?.is_ketua,
                    jumlah_total_anggota:
                      item.details_parsed?.jumlah_total_anggota,
                  },
                  is_taken:
                    !!pub.transfer_to ||
                    !!pub.iku_terpilih ||
                    !!pub.is_taken ||
                    item.details_parsed?.is_taken ||
                    false,
                  iku_terpilih:
                    pub.transfer_to ||
                    pub.iku_terpilih ||
                    item.details_parsed?.transfer_to ||
                    item.details_parsed?.iku_terpilih ||
                    null,
                  raw_details: item.details || "",
                }));
              }

              return {
                url: item.details || item.url || item.details_parsed?.file_path,
                url_file_path:
                  item.url || item.details_parsed?.file_path || item.details,
                sks_judul:
                  item.details_parsed?.no_sk ||
                  item.details?.split("$")[0] ||
                  "SK",
                is_iku: item.is_iku,
                iku: item.iku,
                details_parsed: item.details_parsed,
                is_taken:
                  !!item.details_parsed?.transfer_to ||
                  !!item.details_parsed?.iku_terpilih ||
                  item.details_parsed?.is_taken ||
                  false,
                iku_terpilih:
                  item.details_parsed?.transfer_to ||
                  item.details_parsed?.iku_terpilih ||
                  null,
                raw_details: item.details,
              };
            })
            .flat();
        }
      }

      let ikuInfo: any = null;
      if (Array.isArray(validationData)) {
        ikuInfo = validationData.find((d: any) => d.is_iku);
      }

      const isPenelitian =
        unsurPenelitianData &&
        Object.prototype.hasOwnProperty.call(unsurPenelitianData, key);

      const isPengabdian =
        (data?.details_pengabdian || []).some(
          (d: any) =>
            d.kode?.toUpperCase() === key.replace("ren_", "REN").toUpperCase(),
        ) ||
        (data?.detail_pengabdian || []).some(
          (d: any) =>
            d.kode?.toUpperCase() === key.replace("ren_", "REN").toUpperCase(),
        );

      const isSeminarProposal =
        unsurSeminarProposalData &&
        Object.prototype.hasOwnProperty.call(unsurSeminarProposalData, key);

      const isMengujiProposal =
        unsurMengujiProposalData &&
        Object.prototype.hasOwnProperty.call(unsurMengujiProposalData, key);

      const isMembimbingTa =
        unsurMembimbingTaData &&
        Object.prototype.hasOwnProperty.call(unsurMembimbingTaData, key);

      const isMengujiTa =
        unsurMengujiTaData &&
        Object.prototype.hasOwnProperty.call(unsurMengujiTaData, key);

      const isPenunjang =
        unsurPenunjangData &&
        Object.prototype.hasOwnProperty.call(unsurPenunjangData, key);

      const isPembimbingAkademik =
        unsurPembimbingAkademikData &&
        Object.prototype.hasOwnProperty.call(unsurPembimbingAkademikData, key);

      const isPendidikan =
        unsurPendidikanData &&
        Object.prototype.hasOwnProperty.call(unsurPendidikanData, key);

      const converted_to_sks = isPendidikan
        ? (Number(item?.total_sks) || 0) * (Number(item?.point_sks) || 0)
        : Number(item?.sks_tertimbang) || 0;

      const point_hasil_final_bulan = isJabatanTambahan
        ? 0
        : Number(item?.point_hasil_final_bulan) || 0;

      return {
        kode: key,
        kegiatan: title,
        sumber: Number(item?.total_sks) || 0,
        nilai_max: Number(item?.nilai_max) || 0,
        point_sks_pengali: Number(item?.point_sks_final) || 0,
        point_hasil_final_bulan,
        validationData,
        isJabatanTambahan,
        isSeminarProposal,
        isMengujiProposal,
        isMembimbingTa,
        isMengujiTa,
        isPenunjang,
        isPembimbingAkademik,
        is_iku: ikuInfo?.is_iku || false,
        iku: ikuInfo?.iku || null,
        is_taken: ikuInfo?.is_taken || false,
        isPenelitian,
        isPengabdian,
        isPendidikan,
        jenis_bukti: isPenelitian ? "Eviden" : "SK",
        point_sks: Number(item?.point_sks) || 0,
        converted_to_sks,
        index: Number(item?.index) || 0,
        dipotong_bkd: 0,
      };
    });

  const groupedPerformanceData = (() => {
    if (!masterPeriodeDetail?.datas || masterPeriodeDetail.datas.length === 0) {
      return [
        {
          unsur: { nama_unsur: "Performa Kinerja" },
          items: performanceData,
        },
      ];
    }

    const komponenToUnsur = new Map<string, any>();
    masterPeriodeDetail.datas.forEach((unsur) => {
      unsur.details.forEach((d) => {
        if (d.detail?.kode_komponen) {
          komponenToUnsur.set(d.detail.kode_komponen.toUpperCase(), unsur);
        }
      });
    });

    const groupsMap = new Map<string, { unsur: any; items: any[] }>();
    const unmappedGroupId = "UNMAPPED";

    performanceData.forEach((item) => {
      const normalizedKode = item.kode.replace("_", "").toUpperCase();
      const unsur = komponenToUnsur.get(normalizedKode);

      const groupId = unsur ? unsur.kode_unsur : unmappedGroupId;
      if (!groupsMap.has(groupId)) {
        groupsMap.set(groupId, {
          unsur: unsur || { nama_unsur: "Lain-lain" },
          items: [],
        });
      }
      groupsMap.get(groupId)!.items.push(item);
    });

    return Array.from(groupsMap.values());
  })();

  const hasPenelitian = (data?.details_penelitian?.length ?? 0) > 0;
  const hasPengabdian =
    (data?.details_pengabdian?.length ?? 0) > 0 ||
    (data?.detail_pengabdian?.length ?? 0) > 0;
  // const hasPenelitian = true;
  // const hasPengabdian = true;

  const totalSksPendidikan = performanceData
    .filter((p) => p.isPendidikan)
    .reduce((acc, curr) => acc + (curr.converted_to_sks || 0), 0);
  const hasPendidikan = totalSksPendidikan >= 4;

  const totalSksPenunjang = performanceData
    .filter((p) => p.isPenunjang)
    .reduce((acc, curr) => acc + (curr.converted_to_sks || 0), 0);
  const hasPenunjang = totalSksPenunjang >= 0; //BERUBAH UBAH

  const isLolosSyaratMutlak =
    hasPenelitian && hasPengabdian && hasPendidikan && hasPenunjang;

  // Syarat Mutlak (2 SKS)
  const potonganPenelitian = 0; // isLolosSyaratMutlak ? 1 : 0;
  const potonganPengabdian = 0; // isLolosSyaratMutlak ? 1 : 0;

  let potonganPendidikan = 0;
  let potonganPenunjang = 0;
  let potonganLainnya = 0;

  // Pemotong BKD 12 SKS telah dihilangkan sesuai permintaan
  /*
  if (isLolosSyaratMutlak) {
    let remainingGeneralDeduction =
      12 - potonganPenelitian - potonganPengabdian; // 10 SKS

    // 1. Potongan Pendidikan (Wajib 4 SKS) -> Sort ascending index
    let reqPendidikan = 4;
    const pendidikanItems = performanceData
      .filter((p) => p.isPendidikan)
      .sort((a, b) => a.index - b.index);

    for (const pItem of pendidikanItems) {
      if (reqPendidikan > 0) {
        const dipotong = Math.min(pItem.converted_to_sks, reqPendidikan);
        pItem.dipotong_bkd = (pItem.dipotong_bkd || 0) + dipotong;
        reqPendidikan -= dipotong;
        potonganPendidikan += dipotong;
        remainingGeneralDeduction -= dipotong;
      }
    }

    // 2. Potongan Penunjang (Max 3 SKS) -> Sort ascending index
    let reqPenunjang = 3;
    const penunjangItems = performanceData
      .filter((p) => p.isPenunjang)
      .sort((a, b) => a.index - b.index);

    for (const pItem of penunjangItems) {
      if (reqPenunjang > 0) {
        const dipotong = Math.min(pItem.converted_to_sks, reqPenunjang);
        pItem.dipotong_bkd = (pItem.dipotong_bkd || 0) + dipotong;
        reqPenunjang -= dipotong;
        potonganPenunjang += dipotong;
        remainingGeneralDeduction -= dipotong;
      }
    }

    // 3. Potongan Lainnya (Sisa BKD dari pool manapun)
    if (remainingGeneralDeduction > 0) {
      const generalItems = performanceData.filter(
        (p) =>
          !p.isPenelitian &&
          !p.isPengabdian &&
          !p.isPenunjang &&
          !p.isPendidikan,
      );

      for (const gItem of generalItems) {
        if (remainingGeneralDeduction > 0) {
          const sisaItem = gItem.converted_to_sks - (gItem.dipotong_bkd || 0);
          if (sisaItem > 0) {
            const dipotong = Math.min(sisaItem, remainingGeneralDeduction);
            gItem.dipotong_bkd = (gItem.dipotong_bkd || 0) + dipotong;
            remainingGeneralDeduction -= dipotong;
            potonganLainnya += dipotong;
          }
        }
      }

      if (remainingGeneralDeduction > 0) {
        for (const pItem of pendidikanItems) {
          if (remainingGeneralDeduction > 0) {
            const sisaItem = pItem.converted_to_sks - (pItem.dipotong_bkd || 0);
            if (sisaItem > 0) {
              const dipotong = Math.min(sisaItem, remainingGeneralDeduction);
              pItem.dipotong_bkd = (pItem.dipotong_bkd || 0) + dipotong;
              remainingGeneralDeduction -= dipotong;
              potonganLainnya += dipotong;
            }
          }
        }
      }
    }
  }
  */

  let sisaSksTotal = 0;
  let sisaSksPendidikan = 0;
  let sisaSksLainnya = 0;
  let sisaSksPenunjang = 0;

  let sisaSksLainnyaIndexed = 0;
  let sisaSksPenunjangIndexed = 0;

  if (isLolosSyaratMutlak) {
    // Calculate accumulated indexed points for remaining SKS
    for (const item of performanceData) {
      const sisa = item.converted_to_sks - (item.dipotong_bkd || 0);
      const tertimbang = sisa * (item.index || 1);

      sisaSksTotal += sisa;

      if (item.isPenunjang) {
        sisaSksPenunjang += sisa;
        sisaSksPenunjangIndexed += tertimbang;
      } else {
        if (item.isPendidikan) {
          sisaSksPendidikan += sisa;
        } else {
          sisaSksLainnya += sisa;
        }
        sisaSksLainnyaIndexed += tertimbang;
      }
    }
  }

  const sksDiakui = isLolosSyaratMutlak ? sisaSksTotal : 0;

  const getFungsionalCode = (jabatan?: string) => {
    if (!jabatan) return "TP";
    const j = jabatan.toLowerCase();
    if (j.includes("guru besar")) return "GB";
    if (j.includes("lektor kepala")) return "LK";
    if (j.includes("lektor")) return "L";
    if (j.includes("asisten ahli")) return "AA";
    return "TP";
  };

  const fungsionalCode = getFungsionalCode(
    currentMonthData?.jabalan_fungsional ||
      currentMonthData?.jabatan_fungsional,
  );
  const maxSksObj = masterKonstantaData?.find(
    (k) => k.fungsional === fungsionalCode,
  );
  const maxSks = maxSksObj ? maxSksObj.konstanta : null;

  const totalPotonganBkd =
    potonganPenelitian +
    potonganPengabdian +
    potonganPendidikan +
    potonganPenunjang +
    potonganLainnya;
  const isBKDFulfilled = totalPotonganBkd >= 0;
  console.log(isBKDFulfilled);

  const pointSKSFinal = konstantaFungsionalData?.pointSKSFinal || 0;
  const nilaiFungsional = Number(currentMonthData?.nilai_fungsional) || 0;

  const persentaseBkd = configPersentase?.persentase_bkd ?? 0.6;
  const {
    rawPoinPenunjang, rawPoinLainnya, maxPoinPenunjang, maxPoinLainnya,
    poinPenunjangDiakui, poinLainnyaDiakui, poinKinerjaDasar,
  } = calculateCapaianKinerja({
    nilaiFungsional, persentaseBkd, pointSKSFinal,
    sksLainnyaIndexed: sisaSksLainnyaIndexed,
    sksPenunjangIndexed: sisaSksPenunjangIndexed,
    isLolosSyaratMutlak,
  });

  const handleExportExcel = async () => {
    if (isExportingExcel || !mappedMonths.length) return;
    setIsExportingExcel(true);
    try {
      const monthlyData: MonthlyExportData[] = [];
      const performanceExport = await queryClient.fetchQuery({
        queryKey: ["export-70-new", niu, year, semester, periodeId],
        queryFn: () => getExport70New({ niu: niu as string, year, semester, periodeID: periodeId }),
      });
      // Fetch every filter month independently; never reuse the selected month's rate.
      for (const month of mappedMonths) {
        const [konstanta, akumulasi, iku6] = await Promise.all([
          queryClient.fetchQuery({
            queryKey: ["konstanta-fungsional", niu, year, semester, periodeId, month.month],
            queryFn: () => getKonstantaFungsional({ niu: niu as string, year, semester, periodeID: periodeId, month: month.month }),
          }),
          queryClient.fetchQuery({
            queryKey: ["akumulasi-iku", niu, year, semester, periodeId, month.month],
            queryFn: () => getAkumulasiIku(niu as string, year, semester, periodeId, month.month),
          }),
          queryClient.fetchQuery({
            queryKey: ["iku-6-penelitian", niu, year, semester, periodeId, month.month],
            queryFn: () => getIku6(niu as string, year, semester, periodeId, month.month),
          }),
        ]);
        if (!konstanta || !akumulasi?.datas || !iku6) {
          throw new Error(`Data ekspor bulan ${month.month} belum tersedia`);
        }
        monthlyData.push({ month, pointSKSFinal: Number(konstanta.pointSKSFinal) || 0,
          akumulasi: akumulasi.datas,
          iku6RealPoint: Number(iku6.iku_list?.find((item: { iku_code: string; real_point?: number }) => item.iku_code === "IKU 6")?.real_point) || 0,
        });
      }
      if (!Array.isArray(performanceExport)) throw new Error("Data rincian per bulan belum tersedia");
      const workbook = createRemunDetailWorkbook({
        nama: data.nama, niu: data.niu, nip: data.nip, year, semester,
        months: monthlyData, performanceGroups: groupedPerformanceData,
        export70Data: performanceExport, pir, persentaseBkd,
        sksLainnyaIndexed: sisaSksLainnyaIndexed,
        sksPenunjangIndexed: sisaSksPenunjangIndexed,
        isLolosSyaratMutlak, showNilai, showRupiah,
        canViewRupiah: isAdmin || isAdminUniv || (isDosen && showNilai),
      });
      writeFile(workbook, `Remunerasi_Dosen_${data.niu}_${year}_${semester}.xlsx`);
      toast.success("Excel remunerasi berhasil diekspor");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Gagal mengekspor Excel remunerasi");
    } finally {
      setIsExportingExcel(false);
    }
  };

  const finalTotalPointDiperoleh = isLolosSyaratMutlak
    ? totalPointDiperoleh
    : 0;
  const totalPointKinerja = poinKinerjaDasar + finalTotalPointDiperoleh;

  const poinStruktural = Number(
    (
      (Number(currentMonthData?.nilai_struktural) || 0) *
      ((Number(currentMonthData?.cluster) || 0) / 100)
    ).toFixed(2),
  );
  const totalPoinRemunerasi70 = totalPointKinerja + poinStruktural;

  const renderPointTerhitung = (
    capaian: number,
    maxPoint: number,
    diakui: number,
  ): React.ReactNode => {
    if (!showNilai) return "***";
    const isExceeded = nilaiFungsional > 0 && capaian > maxPoint;
    if (isExceeded) {
      if (isDosen) {
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] sm:text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200/60 shadow-sm select-none">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
            Maksimal
          </span>
        );
      }
      return (
        <div className="flex flex-col items-end gap-0.5">
          <span className="font-semibold text-slate-800">
            {roundNumber(capaian, 2)}
          </span>
          <span className="inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200/60 text-[9px] font-medium leading-none select-none">
            <span className="w-1 h-1 rounded-full bg-emerald-500 animate-pulse"></span>
            Maksimal ({roundNumber(diakui, 2)})
          </span>
        </div>
      );
    }
    return roundNumber(capaian, 2);
  };

  // let currentMonthName = "";
  // if (currentMonthData?.month) {
  //   const [y, m] = currentMonthData.month.split("_");
  //   currentMonthName = new Date(parseInt(y), parseInt(m) - 1, 1).toLocaleString(
  //     "id-ID",
  //     { month: "long", year: "numeric" },
  //   );
  // }

  const totalSummary = Object.entries(data?.data_per_bulan || {}).reduce(
    (acc: any, [monthKey, item]: [string, any]) => {
      const isTugasBelajar =
        item.status_sekarang?.toLowerCase() === "tugas belajar";

      let isBelumMenerimaRemun = false;
      if (item.tgl_mulai_pembayaran_remun && monthKey) {
        const [y, m] = monthKey.split("_");
        const tglMulai = new Date(item.tgl_mulai_pembayaran_remun);
        const tglMulaiValue =
          tglMulai.getFullYear() * 100 + (tglMulai.getMonth() + 1);
        const itemMonthValue = parseInt(y) * 100 + parseInt(m);
        if (tglMulaiValue > itemMonthValue) {
          isBelumMenerimaRemun = true;
        }
      }

      const monthDataExport = export70Data?.find(
        (d: any) => d.Bulan === monthKey,
      );
      const dynamicPointKinerja = monthDataExport
        ? Number(monthDataExport.PointPerformance)
        : totalPointKinerja;

      const effectivePointKinerja =
        isTugasBelajar || isBelumMenerimaRemun ? 0 : dynamicPointKinerja;

      const calculatedTotalPoin =
        isTugasBelajar || isLolosSyaratMutlak === false
          ? 0
          : Number(
              (
                item.nilai_struktural * (Number(item.cluster) / 100) +
                effectivePointKinerja
              ).toFixed(2),
            );
      let multiplier = 1;
      const isKontrak = item.status_pegawai?.toUpperCase() === "KONTRAK";
      const isPensiun = item.status_sekarang?.toLowerCase() === "pensiun";

      if (item.status_pegawai?.toUpperCase() === "PPPK") {
        multiplier = 0.6;
      } else if (item.status_pegawai?.toUpperCase() === "CPNS") {
        multiplier = 0.8;
      } else if (isKontrak || isPensiun) {
        multiplier = 0;
      }

      const isNoRemun = isKontrak || isPensiun;

      const remunKotor100 = isNoRemun
        ? 0
        : calculatedTotalPoin * pir * multiplier;

      const remunKotor30 = isNoRemun
        ? 0
        : item.rupiah_realisasi_30 !== null &&
            item.rupiah_realisasi_30 !== undefined
          ? Number(item.rupiah_realisasi_30)
          : 0;
      const rupiahSerdos = isNoRemun ? 0 : Number(item.rupiah_serdos || 0);
      const remunKotor70Base = isNoRemun
        ? 0
        : remunKotor100 - rupiahSerdos - remunKotor30;
      const remunKotor70 = isNoRemun ? 0 : Math.max(0, remunKotor70Base);
      const pphPersen = Number(item.pph_21 || 0); // Keep pphPersen as is so it doesn't break display logic
      const pphNominal = isNoRemun ? 0 : remunKotor70 * pphPersen;
      const remunBersih70 = isNoRemun
        ? 0
        : Math.max(0, remunKotor70 - pphNominal);

      acc.remunKotor100 += remunKotor100;
      acc.rupiahSerdos += rupiahSerdos;
      acc.remunKotor30 += remunKotor30;
      acc.remunKotor70 += remunKotor70;
      acc.pphNominal += pphNominal;
      acc.remunBersih70 += remunBersih70;
      acc.pphPersen = pphPersen; // we just take the last month's pph_21 for display

      return acc;
    },
    {
      remunKotor100: 0,
      rupiahSerdos: 0,
      remunKotor30: 0,
      remunKotor70: 0,
      pphNominal: 0,
      remunBersih70: 0,
      pphPersen: 0,
    },
  );

  // --- Calculate Global Validation Status ---
  const currentUserRole = user?.role?.replace(/\s+/g, "_").toLowerCase() || "";

  let searchStr = "dosen";
  let fallbackIdx = 0;
  let roleDisplayName = "Dosen";

  if (currentUserRole === "admin_fakultas") {
    searchStr = "fakultas";
    fallbackIdx = 1;
    roleDisplayName = "Fakultas";
  } else if (currentUserRole === "admin_univ") {
    searchStr = "rektorat";
    fallbackIdx = 2;
    roleDisplayName = "Universitas";
  }

  let isRincianPerBulanValid = true;
  const invalidRincianItems: any[] = [];

  mappedMonths.forEach((item: any) => {
    if (
      item.status_pegawai?.toLowerCase() === "kontrak" ||
      item.status_pegawai?.toLowerCase() === "luar biasa" ||
      item.status_sekarang?.toLowerCase() === "pensiun" ||
      item.status_sekarang?.toLowerCase() === "tugas belajar"
    ) {
      return;
    }

    const isHtlValid =
      item.validator_details?.find((v: any) => v.level === "admin_htl")
        ?.is_valid_taken === true;
    const isKepegValid =
      item.validator_details?.find((v: any) => v.level === "admin_kepegawaian")
        ?.is_valid_taken === true;
    const isDosenValid =
      item.validator_details?.find((v: any) => v.level === "dosen")
        ?.is_valid_taken === true;

    const roleVal = item.validator_details?.find(
      (v: any) =>
        v.level.replace(/\s+/g, "_").toLowerCase() === currentUserRole,
    );

    // Memastikan sudah valid hingga level dosen (HTL, Kepeg, Dosen)
    // Serta memastikan valid untuk role yang sedang login
    if (
      !isHtlValid ||
      !isKepegValid ||
      !isDosenValid ||
      !roleVal ||
      roleVal.is_valid_taken !== true
    ) {
      isRincianPerBulanValid = false;
      invalidRincianItems.push({
        month: item.month_name || item.month,
        item,
      });
    }
  });

  const checkCanValidateRow = (row: any, userRole: string | undefined) => {
    const isUnsurPendidikan = [
      "ren_1",
      "ren_2",
      "ren_3",
      "ren_162",
      "ren_226",
      "ren_227",
      "ren_228",
      "ren_229",
    ].includes(row.kode);

    const isInvalidType =
      (!isUnsurPendidikan &&
        !row.isSeminarProposal &&
        !row.isMengujiProposal &&
        !row.isMembimbingTa &&
        !row.isMengujiTa &&
        !row.isPenunjang &&
        !row.isPembimbingAkademik &&
        !row.isPenelitian) ||
      !row.validationData ||
      row.validationData.length === 0;

    if (isInvalidType) return false;

    const isValidationDataEmpty = isUnsurPendidikan && row.sumber === 0;

    if (isValidationDataEmpty) {
      return false;
    }

    const d = row.validationData[0];
    const details =
      d?.details_parsed?.validator_details ||
      d?.validator_details ||
      d?.detail?.validator_details ||
      [];

    const dosenMatch = details.find((d: any) =>
      d.level?.toLowerCase().includes("dosen"),
    );
    const isDosenValid = dosenMatch
      ? dosenMatch.is_valid_taken
      : details[0]?.is_valid_taken;

    const fakultasMatch = details.find((d: any) =>
      d.level?.toLowerCase().includes("fakultas"),
    );
    const isFakultasValid = fakultasMatch
      ? fakultasMatch.is_valid_taken
      : details[1]?.is_valid_taken;

    if (userRole === "dosen") {
      return true;
    } else if (userRole === "admin_fakultas") {
      return !!isDosenValid;
    } else if (userRole === "admin_univ") {
      return !!isFakultasValid;
    }

    return false;
  };

  let isKinerjaValid = true;
  const invalidKinerjaItems: any[] = [];
  const validatableRows = performanceData.filter((row: any) =>
    checkCanValidateRow(row, user?.role),
  );

  validatableRows.forEach((item: any) => {
    // Mengecek apakah ada minimal satu data valid di validationData menggunakan .some()
    // Iterasi akan berhenti segera setelah menemukan satu data yang valid (return true)
    const hasValidData = item.validationData?.some((d: any) => {
      const details =
        d.validator_details ||
        d.detail?.validator_details ||
        d.details_parsed?.validator_details ||
        [];

      const dosenMatch = details.find((v: any) =>
        v.level?.toLowerCase().includes("dosen"),
      );
      const isDosenValid = dosenMatch
        ? dosenMatch.is_valid_taken
        : details[0]?.is_valid_taken;

      const fakultasMatch = details.find((v: any) =>
        v.level?.toLowerCase().includes("fakultas"),
      );
      const isFakultasValid = fakultasMatch
        ? fakultasMatch.is_valid_taken
        : details[1]?.is_valid_taken;

      const isFakultasDisabled =
        currentUserRole === "admin_fakultas" && !isDosenValid;
      const isAdminUnivDisabled =
        currentUserRole === "admin_univ" && !isFakultasValid;

      const match = details.find((v: any) =>
        v.level?.toLowerCase().includes(searchStr),
      );
      const val = match
        ? match.is_valid_taken
        : details[fallbackIdx]?.is_valid_taken;

      if (!isFakultasDisabled && !isAdminUnivDisabled) {
        return val === true; // return true jika valid, .some() akan langsung berhenti
      }

      // Jika disable (skip), kita anggap true agar tidak memblokir
      return true;
    });

    // Jika tidak ada data yang valid, dan validationData tidak kosong
    if (
      !hasValidData &&
      item.validationData &&
      item.validationData.length > 0
    ) {
      isKinerjaValid = false;
      if (!invalidKinerjaItems.find((i) => i.kode === item.kode)) {
        invalidKinerjaItems.push(item);
      }
    }
  });

  let isGlobalValidFakultas = true;
  let isGlobalValidUniv = true;
  let hasAnyValidatableFakultasRow = false;
  const invalidGlobalFakultasItems: any[] = [];
  const invalidGlobalUnivItems: any[] = [];

  performanceData.forEach((row: any) => {
    const isUnsurPendidikan = [
      "ren_1",
      "ren_2",
      "ren_3",
      "ren_162",
      "ren_226",
      "ren_227",
      "ren_228",
      "ren_229",
    ].includes(row.kode);

    const isInvalidType =
      (!isUnsurPendidikan &&
        !row.isSeminarProposal &&
        !row.isMengujiProposal &&
        !row.isMembimbingTa &&
        !row.isMengujiTa &&
        !row.isPenunjang &&
        !row.isPembimbingAkademik &&
        !row.isPenelitian) ||
      !row.validationData ||
      row.validationData.length === 0;

    if (isInvalidType) return;

    const isValidationDataEmpty = isUnsurPendidikan && row.sumber === 0;

    if (isValidationDataEmpty) {
      return;
    }

    hasAnyValidatableFakultasRow = true;

    const hasBothValid = row.validationData.some((d: any) => {
      const details =
        d.validator_details ||
        d.detail?.validator_details ||
        d.details_parsed?.validator_details ||
        [];

      const dosenMatch = details.find((v: any) =>
        v.level?.toLowerCase().includes("dosen"),
      );
      const isDosenValid = dosenMatch
        ? dosenMatch.is_valid_taken
        : details[0]?.is_valid_taken;

      const fakultasMatch = details.find((v: any) =>
        v.level?.toLowerCase().includes("fakultas"),
      );
      const isFakultasValid = fakultasMatch
        ? fakultasMatch.is_valid_taken
        : details[1]?.is_valid_taken;

      return isDosenValid === true && isFakultasValid === true;
    });

    const hasAllThreeValid = row.validationData.some((d: any) => {
      const details =
        d.validator_details ||
        d.detail?.validator_details ||
        d.details_parsed?.validator_details ||
        [];

      const dosenMatch = details.find((v: any) =>
        v.level?.toLowerCase().includes("dosen"),
      );
      const isDosenValid = dosenMatch
        ? dosenMatch.is_valid_taken
        : details[0]?.is_valid_taken;

      const fakultasMatch = details.find((v: any) =>
        v.level?.toLowerCase().includes("fakultas"),
      );
      const isFakultasValid = fakultasMatch
        ? fakultasMatch.is_valid_taken
        : details[1]?.is_valid_taken;

      const univMatch = details.find((v: any) =>
        v.level?.toLowerCase().includes("univ"),
      );
      const isUnivValid = univMatch
        ? univMatch.is_valid_taken
        : details[2]?.is_valid_taken;

      return (
        isDosenValid === true &&
        isFakultasValid === true &&
        isUnivValid === true
      );
    });

    if (!hasBothValid) {
      isGlobalValidFakultas = false;
      if (!invalidGlobalFakultasItems.find((i) => i.kode === row.kode)) {
        invalidGlobalFakultasItems.push(row);
      }
    }

    if (!hasAllThreeValid) {
      isGlobalValidUniv = false;
      if (!invalidGlobalUnivItems.find((i) => i.kode === row.kode)) {
        invalidGlobalUnivItems.push(row);
      }
    }
  });

  if (!hasAnyValidatableFakultasRow) {
    isGlobalValidFakultas = false;
    isGlobalValidUniv = false;
  }

  const isGlobalValid = isAlreadyConfirmed
    ? isAlreadyConfirmed
    : isRincianPerBulanValid && isKinerjaValid;

  const hasNewUnvalidatedData = false;
  // isAlreadyConfirmed && (!isRincianPerBulanValid || !isKinerjaValid);

  const handleKonfirmasiData = () => {
    const payload = {
      per_unsur_status: {
        unsur_pelaksanaan_pendidikan: true,
        unsur_membimbing_seminar_proposal: true,
        unsur_menguji_proposal: true,
        unsur_membimbing_ta: true,
        unsur_menguji_ta: true,
        unsur_penunjang: true,
        unsur_produk_penelitian_pengabdian: true,
        unsur_pembimbing_akademik: true,
        unsur_jabatan_tambahan: true,
        dosen_remun: true,
      },
    };

    validationStatusMutation.mutate({
      year,
      semester,
      niu: niu || data?.nip,
      is_validator: true,
      payload,
    });
  };

  return (
    <TooltipProvider>
      <Dialog open={showDosenPopup} onOpenChange={setShowDosenPopup}>
        <DialogContent className="max-w-md sm:max-w-xl max-h-[85vh] flex flex-col overflow-hidden p-0 gap-0">
          <div className="p-6 pb-4 shrink-0">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold">
                Informasi Penting
              </DialogTitle>
            </DialogHeader>
          </div>
          <div className="space-y-4 text-sm text-slate-700 overflow-y-auto px-6 pb-6 flex-1">
            <p className="font-semibold">Yth. Bapak/Ibu Dosen,</p>
            <p>
              Sistem Remunerasi saat ini sedang dalam penyesuaian perhitungan
              poin. Bapak/Ibu sudah dapat mengecek data kinerja yang telah masuk
              ke sistem.
            </p>
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 flex gap-4 items-start my-2">
              <div className="bg-indigo-100 p-2 rounded-full shrink-0">
                <FileText className="w-5 h-5 text-indigo-700" />
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-bold text-indigo-900">
                  Panduan Validasi Kinerja
                </h4>
                <p className="text-xs text-indigo-800/80 mt-1 mb-3 leading-relaxed">
                  Bapak/Ibu dapat mengunduh dan membaca panduan validasi kinerja
                  remunerasi tahun 2026 pada tautan di bawah ini.
                </p>
                <a
                  href="/Validasi Kinerja Remunerasi 2026.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 text-xs font-semibold text-white bg-indigo-600 rounded-md hover:bg-indigo-700 transition-colors shadow-sm"
                >
                  <Download className="w-4 h-4" />
                  Unduh Panduan (PDF)
                </a>
              </div>
            </div>
            <p>
              Jika terdapat data yang belum sesuai, mohon lakukan pengecekan
              berikut:
            </p>
            <ul className="list-disc pl-5 space-y-2">
              <li>
                <span className="font-semibold">SKS Mengajar:</span> Pastikan
                agenda perkuliahan/e-learning telah divalidasi dan SK Mengajar
                sudah di-tag.
              </li>
              <li>
                <span className="font-semibold">Penelitian & Pengabdian:</span>{" "}
                Pastikan kontrak dan dokumen revisi telah diunggah pada sistem
                SILIDIA.
              </li>
              <li>
                <span className="font-semibold">
                  Membimbing, Menguji, & Penunjang:
                </span>{" "}
                Pastikan data sudah di-tag pada sistem Shakuntala.
              </li>
              <li>
                <span className="font-semibold">
                  Kinerja Lainnya (Misal: Publikasi):
                </span>{" "}
                Cek status validasi langsung pada sistem Kinerja.
              </li>
            </ul>
            <p className="font-bold text-red-600">
              Batas Akhir Validasi Data: Jumat, 10 Juli 2026 pukul 16.00 WITA.
            </p>
            <p className="font-semibold">Salam Harmoni.</p>
            <p className="mt-4 text-xs font-semibold text-red-600 bg-red-50 p-2 rounded">
              Mohon selalu periksa status validasi dengan seksama.
            </p>
          </div>
          <div className="bg-white px-6 py-4 border-t flex justify-end shrink-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowDosenPopup(false)}
            >
              Tutup
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Konfirmasi Data Popup */}
      <Dialog
        open={showKonfirmasiPopup}
        onOpenChange={(open) => {
          setShowKonfirmasiPopup(open);
          if (!open) setIsKonfirmasiChecked(false);
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Konfirmasi Validasi</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 text-sm text-slate-700">
            <p>
              Dengan ini Anda menyatakan bahwa seluruh data kinerja, rincian
              remunerasi, serta <strong>kesesuaian data jabatan</strong> untuk
              dosen <strong>{data.nama}</strong> telah divalidasi dan dipastikan
              kebenarannya.
            </p>
            <p className="text-amber-600 font-medium bg-amber-50 p-3 rounded-lg border border-amber-200 text-xs">
              Klik "Konfirmasi" akan menandai penyelesaian proses verifikasi
              data ini sesuai dengan kewenangan peran Anda.
            </p>

            <div className="flex items-start gap-3 mt-4 bg-slate-50 p-3 rounded-lg border border-slate-200">
              <input
                type="checkbox"
                id="konfirmasi-checkbox"
                className="mt-0.5 w-4 h-4 text-emerald-600 rounded border-slate-300 focus:ring-emerald-500 cursor-pointer"
                checked={isKonfirmasiChecked}
                onChange={(e) => setIsKonfirmasiChecked(e.target.checked)}
              />
              <label
                htmlFor="konfirmasi-checkbox"
                className="text-xs text-slate-600 leading-relaxed cursor-pointer select-none"
              >
                Saya menyetujui dan bertanggung jawab penuh atas seluruh hasil
                validasi data kinerja dan kesesuaian data jabatan pada halaman
                ini.
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowKonfirmasiPopup(false)}
            >
              Kembali
            </Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:bg-slate-300 disabled:cursor-not-allowed"
              disabled={
                !isKonfirmasiChecked || validationStatusMutation.isPending
              }
              onClick={handleKonfirmasiData}
            >
              {validationStatusMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Memproses...
                </>
              ) : (
                "Ya, Konfirmasi"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="w-full pb-8 flex flex-col min-h-screen">
        <div className="container mx-auto pt-4 px-4 space-y-6 flex-1">
          {/* Header Actions */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <ProtectRole
              allowedRoles={[
                ROLES.ADMIN_SUPER,
                ROLES.ADMIN_UNIV,
                ROLES.ADMIN_FAKULTAS,
              ]}
            >
              <Button
                variant="outline"
                onClick={() => navigate(-1)}
                className="bg-white gap-2 hover:bg-slate-50 transition-colors shadow-sm"
              >
                <ArrowLeft className="w-4 h-4" /> Kembali
              </Button>
            </ProtectRole>
            <ProtectRole allowedRoles={[ROLES.ADMIN_SUPER, ROLES.ADMIN_UNIV, ROLES.ADMIN_FAKULTAS, ROLES.ADMIN_KEPEGAWAIAN, ROLES.ADMIN_HTL, ROLES.DOSEN]}>
              <Button variant="outline" onClick={handleExportExcel} disabled={isExportingExcel || !mappedMonths.length} className="ml-auto bg-white gap-2">
                {isExportingExcel ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                {isExportingExcel ? "Mengekspor..." : "Export Excel"}
              </Button>
            </ProtectRole>
          </div>

          <ProtectRole
            allowedRoles={[
              ROLES.ADMIN_SUPER,
              ROLES.ADMIN_UNIV,
              ROLES.ADMIN_KEPEGAWAIAN,
              ROLES.ADMIN_HTL,
              ROLES.ADMIN_FAKULTAS,
              ROLES.DOSEN,
            ]}
          >
            <>
              {/* Profil Singkat & Total Rekap */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <Card
                  className={`${!isAdmin && !isAdminUniv && !showNilai ? "lg:col-span-3" : "lg:col-span-2"} shadow-sm border-slate-200 bg-white relative overflow-hidden`}
                >
                  <div className="absolute top-0 right-0 p-8 opacity-5 pointer-events-none">
                    <User className="w-48 h-48" />
                  </div>
                  <CardHeader>
                    <CardDescription className="text-primary font-bold uppercase tracking-wider text-xs flex items-center gap-2">
                      <ShieldCheck className="w-4 h-4" /> Detail Remunerasi
                    </CardDescription>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <CardTitle className="text-3xl font-bold text-slate-800">
                          {data.nama}
                        </CardTitle>
                      </div>

                      <ProtectRole allowedRoles={[ROLES.DOSEN]}>
                        <div className="flex flex-col gap-2 w-full sm:w-auto">
                          {isGlobalValid ? (
                            <>
                              <div
                                className={`flex flex-col sm:flex-row sm:items-center gap-3 p-3 rounded-lg border ${
                                  isAlreadyConfirmed
                                    ? "bg-emerald-50 border-emerald-200"
                                    : "bg-amber-50 border-amber-300 shadow-sm"
                                }`}
                              >
                                <div
                                  className={`flex items-center gap-2 font-semibold text-sm ${
                                    isAlreadyConfirmed
                                      ? "text-emerald-700"
                                      : "text-amber-800"
                                  }`}
                                >
                                  {isAlreadyConfirmed ? (
                                    <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                                  ) : (
                                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                                  )}
                                  <span>
                                    {isAlreadyConfirmed
                                      ? `Seluruh Validasi ${roleDisplayName} Selesai`
                                      : `Validasi Selesai, Menunggu Konfirmasi Akhir`}
                                  </span>
                                </div>
                                <div className="relative inline-flex">
                                  {!isAlreadyConfirmed && (
                                    <span className="absolute -inset-0.5 rounded-md border-2 border-amber-500 animate-[ping_1.5s_cubic-bezier(0,0,0.2,1)_infinite] opacity-75"></span>
                                  )}
                                  <Button
                                    size="sm"
                                    onClick={() => setShowKonfirmasiPopup(true)}
                                    disabled={isAlreadyConfirmed}
                                    className={`relative shadow-sm font-medium transition-all ${
                                      isAlreadyConfirmed
                                        ? "bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-80 disabled:cursor-not-allowed"
                                        : "bg-amber-500 hover:bg-amber-600 text-white border border-amber-600"
                                    }`}
                                  >
                                    <ShieldCheck className="w-4 h-4 mr-1.5" />{" "}
                                    {isAlreadyConfirmed
                                      ? "Telah Dikonfirmasi"
                                      : "Konfirmasi Sekarang"}
                                  </Button>
                                </div>
                              </div>

                              {hasNewUnvalidatedData && (
                                <Collapsible className="bg-sky-50 border border-sky-200 rounded-lg overflow-hidden w-full sm:min-w-[320px] shadow-sm">
                                  <CollapsibleTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      className="w-full flex items-center justify-between p-3 h-auto hover:bg-sky-100/50"
                                    >
                                      <div className="flex items-center gap-2 text-sky-700 font-semibold text-sm text-left">
                                        <Info className="w-5 h-5 text-sky-600 shrink-0" />
                                        <span>
                                          Ada Data Baru yang belum tervalidasi (
                                          {invalidRincianItems.length +
                                            invalidKinerjaItems.length}
                                          )
                                        </span>
                                      </div>
                                      <ChevronDown className="w-4 h-4 text-sky-700 opacity-50 shrink-0 ml-2" />
                                    </Button>
                                  </CollapsibleTrigger>
                                  <CollapsibleContent>
                                    <div className="px-3 pb-3 max-h-64 overflow-y-auto space-y-4 pt-1">
                                      {invalidRincianItems.length > 0 && (
                                        <div>
                                          <p className="text-[11px] font-bold text-sky-800 uppercase mb-1">
                                            Rincian Per Bulan
                                          </p>
                                          <ul className="text-xs text-sky-700 list-disc pl-4 space-y-1">
                                            {invalidRincianItems.map(
                                              (inv, i) => (
                                                <li key={i}>
                                                  Bulan {inv.month}
                                                </li>
                                              ),
                                            )}
                                          </ul>
                                        </div>
                                      )}
                                      {invalidKinerjaItems.length > 0 && (
                                        <div>
                                          <p className="text-[11px] font-bold text-sky-800 uppercase mb-1">
                                            Tabel Kinerja
                                          </p>
                                          <ul className="text-xs text-sky-700 list-disc pl-4 space-y-1">
                                            {invalidKinerjaItems.map(
                                              (inv, i) => {
                                                const formattedKode = inv.kode
                                                  .replace(/_/g, "")
                                                  .toUpperCase();
                                                return (
                                                  <li
                                                    key={i}
                                                    className="line-clamp-2"
                                                    title={formattedKode}
                                                  >
                                                    <a
                                                      href={`#row-${inv.kode}`}
                                                      onClick={(e) => {
                                                        e.preventDefault();
                                                        const el =
                                                          document.getElementById(
                                                            `row-${inv.kode}`,
                                                          );
                                                        if (el) {
                                                          el.scrollIntoView({
                                                            behavior: "smooth",
                                                            block: "center",
                                                          });
                                                        }
                                                      }}
                                                      className="hover:underline cursor-pointer font-bold"
                                                    >
                                                      {formattedKode}
                                                    </a>
                                                  </li>
                                                );
                                              },
                                            )}
                                          </ul>
                                        </div>
                                      )}
                                    </div>
                                  </CollapsibleContent>
                                </Collapsible>
                              )}
                            </>
                          ) : (
                            <Collapsible className="bg-amber-50 border border-amber-200 rounded-lg overflow-hidden w-full sm:min-w-[320px]">
                              <CollapsibleTrigger asChild>
                                <Button
                                  variant="ghost"
                                  className="w-full flex items-center justify-between p-3 h-auto hover:bg-amber-100/50"
                                >
                                  <div className="flex items-center gap-2 text-amber-700 font-semibold text-sm text-left">
                                    <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
                                    <span>
                                      Menunggu Validasi {roleDisplayName} (
                                      {invalidRincianItems.length +
                                        invalidKinerjaItems.length}
                                      )
                                    </span>
                                  </div>
                                  <ChevronDown className="w-4 h-4 text-amber-700 opacity-50 shrink-0 ml-2" />
                                </Button>
                              </CollapsibleTrigger>
                              <CollapsibleContent>
                                <div className="px-3 pb-3 max-h-64 overflow-y-auto space-y-4 pt-1">
                                  {invalidRincianItems.length > 0 && (
                                    <div>
                                      <p className="text-[11px] font-bold text-amber-800 uppercase mb-1">
                                        Rincian Per Bulan
                                      </p>
                                      <ul className="text-xs text-amber-700 list-disc pl-4 space-y-1">
                                        {invalidRincianItems.map((inv, i) => (
                                          <li key={i}>Bulan {inv.month}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                  {invalidKinerjaItems.length > 0 && (
                                    <div>
                                      <p className="text-[11px] font-bold text-amber-800 uppercase mb-1">
                                        Tabel Kinerja
                                      </p>
                                      <ul className="text-xs text-amber-700 list-disc pl-4 space-y-1">
                                        {invalidKinerjaItems.map((inv, i) => {
                                          const formattedKode = inv.kode
                                            .replace(/_/g, "")
                                            .toUpperCase();
                                          return (
                                            <li
                                              key={i}
                                              className="line-clamp-2"
                                              title={formattedKode}
                                            >
                                              <a
                                                href={`#row-${inv.kode}`}
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  const el =
                                                    document.getElementById(
                                                      `row-${inv.kode}`,
                                                    );
                                                  if (el) {
                                                    el.scrollIntoView({
                                                      behavior: "smooth",
                                                      block: "center",
                                                    });
                                                  }
                                                }}
                                                className="hover:underline cursor-pointer font-bold"
                                              >
                                                {formattedKode}
                                              </a>
                                            </li>
                                          );
                                        })}
                                      </ul>
                                    </div>
                                  )}
                                </div>
                              </CollapsibleContent>
                            </Collapsible>
                          )}
                          <div className="flex flex-col sm:flex-row items-center gap-2 mt-2 w-full sm:w-auto">
                            <Button
                              variant="outline"
                              className="bg-white hover:bg-slate-50 border-slate-200 shadow-sm w-full sm:w-auto self-start"
                              onClick={() => setOpenPesan(true)}
                            >
                              <MessageSquare className="w-4 h-4 mr-2 text-primary" />{" "}
                              Ajukan Pertanyaan/Masukan
                            </Button>
                            <Button
                              variant="outline"
                              className="bg-white hover:bg-slate-50 border-slate-200 shadow-sm w-full sm:w-auto self-start text-slate-700"
                              onClick={() => setOpenRiwayatPesan(true)}
                            >
                              <List className="w-4 h-4 mr-2 text-slate-500" />{" "}
                              Riwayat Pertanyaan/Masukan
                            </Button>
                          </div>
                        </div>
                      </ProtectRole>

                      <ProtectRole allowedRoles={[ROLES.ADMIN_FAKULTAS]}>
                        <div className="flex flex-col gap-2 w-full sm:w-auto">
                          <div
                            className={`flex items-center gap-2 p-3 rounded-lg border text-sm font-semibold shadow-sm ${
                              isAlreadyConfirmed
                                ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                                : "bg-rose-50 border-rose-200 text-rose-700"
                            }`}
                          >
                            {isAlreadyConfirmed ? (
                              <CheckCircle2 className="w-4 h-4 shrink-0" />
                            ) : (
                              <AlertTriangle className="w-4 h-4 shrink-0" />
                            )}
                            <span>
                              Status Konfirmasi Dosen:{" "}
                              {isAlreadyConfirmed
                                ? "Sudah Konfirmasi"
                                : "Belum Konfirmasi"}
                            </span>
                          </div>

                          {isGlobalValidFakultas ? (
                            <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 rounded-lg border bg-emerald-50 border-emerald-200 shadow-sm">
                              <div className="flex items-center gap-2 font-semibold text-sm text-emerald-700">
                                <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                                <span>
                                  Seluruh data telah divalidasi di level
                                  Fakultas
                                </span>
                              </div>
                            </div>
                          ) : (
                            <Collapsible className="bg-amber-50 border border-amber-200 rounded-lg overflow-hidden w-full sm:min-w-[320px]">
                              <CollapsibleTrigger asChild>
                                <Button
                                  variant="ghost"
                                  className="w-full flex items-center justify-between p-3 h-auto hover:bg-amber-100/50"
                                >
                                  <div className="flex items-center gap-2 text-amber-700 font-semibold text-sm text-left">
                                    <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
                                    <span>
                                      Menunggu Validasi {roleDisplayName} (
                                      {invalidGlobalFakultasItems.length})
                                    </span>
                                  </div>
                                  <ChevronDown className="w-4 h-4 text-amber-700 opacity-50 shrink-0 ml-2" />
                                </Button>
                              </CollapsibleTrigger>
                              <CollapsibleContent>
                                <div className="px-3 pb-3 max-h-64 overflow-y-auto space-y-4 pt-1">
                                  {invalidGlobalFakultasItems.length > 0 && (
                                    <div>
                                      <p className="text-[11px] font-bold text-amber-800 uppercase mb-1">
                                        Tabel Kinerja
                                      </p>
                                      <ul className="text-xs text-amber-700 list-disc pl-4 space-y-1">
                                        {invalidGlobalFakultasItems.map(
                                          (inv, i) => {
                                            const formattedKode = inv.kode
                                              .replace(/_/g, "")
                                              .toUpperCase();
                                            return (
                                              <li
                                                key={i}
                                                className="line-clamp-2"
                                                title={formattedKode}
                                              >
                                                <a
                                                  href={`#row-${inv.kode}`}
                                                  onClick={(e) => {
                                                    e.preventDefault();
                                                    const el =
                                                      document.getElementById(
                                                        `row-${inv.kode}`,
                                                      );
                                                    if (el) {
                                                      el.scrollIntoView({
                                                        behavior: "smooth",
                                                        block: "center",
                                                      });
                                                    }
                                                  }}
                                                  className="hover:underline cursor-pointer font-bold"
                                                >
                                                  {formattedKode}
                                                </a>
                                              </li>
                                            );
                                          },
                                        )}
                                      </ul>
                                    </div>
                                  )}
                                </div>
                              </CollapsibleContent>
                            </Collapsible>
                          )}
                        </div>
                      </ProtectRole>

                      <ProtectRole allowedRoles={[ROLES.ADMIN_UNIV]}>
                        <div className="flex flex-col gap-2 w-full sm:w-auto">
                          {isGlobalValidUniv ? (
                            <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 rounded-lg border bg-emerald-50 border-emerald-200 shadow-sm">
                              <div className="flex items-center gap-2 font-semibold text-sm text-emerald-700">
                                <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                                <span>
                                  Seluruh data telah divalidasi di level
                                  Universitas
                                </span>
                              </div>
                            </div>
                          ) : (
                            <Collapsible className="bg-amber-50 border border-amber-200 rounded-lg overflow-hidden w-full sm:min-w-[320px]">
                              <CollapsibleTrigger asChild>
                                <Button
                                  variant="ghost"
                                  className="w-full flex items-center justify-between p-3 h-auto hover:bg-amber-100/50"
                                >
                                  <div className="flex items-center gap-2 text-amber-700 font-semibold text-sm text-left">
                                    <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
                                    <span>
                                      Menunggu Validasi {roleDisplayName} (
                                      {invalidGlobalUnivItems.length})
                                    </span>
                                  </div>
                                  <ChevronDown className="w-4 h-4 text-amber-700 opacity-50 shrink-0 ml-2" />
                                </Button>
                              </CollapsibleTrigger>
                              <CollapsibleContent>
                                <div className="px-3 pb-3 max-h-64 overflow-y-auto space-y-4 pt-1">
                                  {invalidGlobalUnivItems.length > 0 && (
                                    <div>
                                      <p className="text-[11px] font-bold text-amber-800 uppercase mb-1">
                                        Tabel Kinerja
                                      </p>
                                      <ul className="text-xs text-amber-700 list-disc pl-4 space-y-1">
                                        {invalidGlobalUnivItems.map(
                                          (inv, i) => {
                                            const formattedKode = inv.kode
                                              .replace(/_/g, "")
                                              .toUpperCase();
                                            return (
                                              <li
                                                key={i}
                                                className="line-clamp-2"
                                                title={formattedKode}
                                              >
                                                <a
                                                  href={`#row-${inv.kode}`}
                                                  onClick={(e) => {
                                                    e.preventDefault();
                                                    const el =
                                                      document.getElementById(
                                                        `row-${inv.kode}`,
                                                      );
                                                    if (el) {
                                                      el.scrollIntoView({
                                                        behavior: "smooth",
                                                        block: "center",
                                                      });
                                                    }
                                                  }}
                                                  className="hover:underline cursor-pointer font-bold"
                                                >
                                                  {formattedKode}
                                                </a>
                                              </li>
                                            );
                                          },
                                        )}
                                      </ul>
                                    </div>
                                  )}
                                </div>
                              </CollapsibleContent>
                            </Collapsible>
                          )}
                        </div>
                      </ProtectRole>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-x-8 gap-y-5 mt-2">
                      <div>
                        <p className="text-xs text-slate-400 font-medium uppercase tracking-wider mb-1">
                          NIP
                        </p>
                        <p className="font-semibold text-slate-700">
                          {data.nip}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400 font-medium uppercase tracking-wider mb-1">
                          NIU
                        </p>
                        <p className="font-semibold text-slate-700">
                          {data.niu}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400 font-medium uppercase tracking-wider mb-1">
                          Jurusan / Fakultas
                        </p>
                        <div className="flex flex-col gap-1.5 mt-1">
                          <p className="font-semibold text-slate-700 leading-tight">
                            {data.unit_homebase?.prodi ||
                              data.unit_homebase?.jurusan ||
                              "-"}{" "}
                            / {data.unit_homebase?.fakultas || "-"}{" "}
                            <span className="text-[10px] font-medium text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded ml-1 uppercase tracking-wider">
                              Homebase
                            </span>
                          </p>
                          {/* <p className="font-semibold text-slate-700 leading-tight">
                            {data.unit?.prodi || data.unit?.jurusan || "-"} /{" "}
                            {data.unit?.fakultas || "-"}{" "}
                            <span className="text-[10px] font-medium text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded ml-1 uppercase tracking-wider">
                              Akreditasi
                            </span>
                          </p> */}
                        </div>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400 font-medium uppercase tracking-wider mb-1">
                          Periode
                        </p>
                        <p className="font-semibold text-slate-700 flex items-center gap-1.5 whitespace-nowrap">
                          <Calendar className="w-3.5 h-3.5 text-slate-400" />{" "}
                          {data.tahun} / Sem. {data.semester}
                        </p>
                      </div>
                      {currentMonthData?.jabatan_struktural != "-" && (
                        <div>
                          <p className="text-xs text-slate-400 font-medium uppercase tracking-wider mb-1">
                            Cluster
                          </p>
                          <Badge
                            variant="outline"
                            className="bg-emerald-50 text-emerald-600 border-emerald-200"
                          >
                            {cluster}%
                          </Badge>
                        </div>
                      )}
                      <div>
                        <p className="text-xs text-slate-400 font-medium uppercase tracking-wider mb-1">
                          Status Pegawai
                        </p>
                        <Badge
                          variant="outline"
                          className="bg-purple-50 text-purple-600 border-purple-200"
                        >
                          {statusPegawai}
                        </Badge>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400 font-medium uppercase tracking-wider mb-1">
                          Status Sekarang
                        </p>
                        <Badge
                          variant="outline"
                          className={
                            statusSekarang === "Aktif"
                              ? "bg-secondary text-primary border-primary/20"
                              : "bg-amber-50 text-amber-600 border-amber-200"
                          }
                        >
                          {statusSekarang}
                        </Badge>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400 font-medium uppercase tracking-wider mb-1">
                          Penelitian
                        </p>
                        {data?.is_penelitian ? (
                          <Badge
                            variant="outline"
                            className="bg-indigo-50 text-indigo-600 border-indigo-200 cursor-pointer hover:bg-indigo-100 transition-colors flex items-center gap-1 w-fit"
                            onClick={() => setOpenPenelitian(true)}
                            title="Klik untuk melihat detail penelitian"
                          >
                            Terpenuhi <ArrowUpRight className="w-3 h-3" />
                          </Badge>
                        ) : (
                          <Badge
                            variant="outline"
                            className="bg-slate-50 text-slate-400 border-slate-200 cursor-not-allowed flex items-center gap-1 w-fit"
                          >
                            Belum Terpenuhi
                          </Badge>
                        )}
                      </div>
                      <div>
                        <p className="text-xs text-slate-400 font-medium uppercase tracking-wider mb-1">
                          Pengabdian
                        </p>
                        {data?.is_pengabdian ? (
                          <Badge
                            variant="outline"
                            className="bg-teal-50 text-teal-600 border-teal-200 cursor-pointer hover:bg-teal-100 transition-colors flex items-center gap-1 w-fit"
                            onClick={() => setOpenPengabdian(true)}
                            title="Klik untuk melihat detail pengabdian"
                          >
                            Terpenuhi <ArrowUpRight className="w-3 h-3" />
                          </Badge>
                        ) : (
                          <Badge
                            variant="outline"
                            className="bg-slate-50 text-slate-400 border-slate-200 cursor-not-allowed flex items-center gap-1 w-fit"
                          >
                            Belum Terpenuhi
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="mt-6 flex items-center gap-2 p-3 bg-slate-50 rounded-lg border border-slate-100 w-fit">
                      <Clock className="w-4 h-4 text-slate-400" />
                      <span className="text-xs text-slate-500 font-medium">
                        Terakhir diperbarui:{" "}
                        <span className="text-slate-700 font-semibold">
                          {new Date(data.last_generated_at).toLocaleString(
                            "id-ID",
                            {
                              dateStyle: "long",
                              timeStyle: "medium",
                            },
                          )}
                        </span>
                      </span>
                    </div>
                  </CardContent>
                </Card>

                <ProtectRole
                  allowedRoles={[
                    ROLES.ADMIN_SUPER,
                    ROLES.ADMIN_UNIV,
                    ...(showNilai ? [ROLES.DOSEN] : []),
                  ]}
                >
                  {/* Ringkasan Total */}
                  <Card className="shadow-sm border-slate-200 bg-linear-to-br from-slate-900 to-slate-800 text-white flex flex-col justify-between relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-6 opacity-5 pointer-events-none">
                      <Wallet className="w-40 h-40" />
                    </div>
                    <CardHeader className="relative z-10">
                      <div className="flex items-start justify-between">
                        <CardDescription className="text-slate-300 font-medium">
                          Total Remunerasi Bersih 70% <br />
                          (6 Bulan)
                        </CardDescription>
                        {isAdmin && (
                          <div className="flex flex-col items-end gap-1.5">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 px-2 text-slate-400 hover:text-white text-[10px]"
                              onClick={() => setShowValidasi(!showValidasi)}
                              title="Toggle Validasi"
                            >
                              {showValidasi ? (
                                <>
                                  <Eye className="h-3.5 w-3.5 mr-1" />
                                  Sembunyikan Validasi
                                </>
                              ) : (
                                <>
                                  <EyeOff className="h-3.5 w-3.5 mr-1" />
                                  Tampilkan Validasi
                                </>
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 px-2 text-slate-400 hover:text-white text-[10px]"
                              onClick={() => setShowNilai(!showNilai)}
                              title="Toggle Nilai Detail"
                            >
                              {showNilai ? (
                                <>
                                  <Eye className="h-3.5 w-3.5 mr-1" />
                                  Sembunyikan Poin
                                </>
                              ) : (
                                <>
                                  <EyeOff className="h-3.5 w-3.5 mr-1" />
                                  Tampilkan Poin
                                </>
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 px-2 text-slate-400 hover:text-white text-[10px]"
                              onClick={() => setShowRupiah(!showRupiah)}
                            >
                              {showRupiah ? (
                                <>
                                  <Eye className="h-3.5 w-3.5 mr-1" />
                                  Sembunyikan Rupiah
                                </>
                              ) : (
                                <>
                                  <EyeOff className="h-3.5 w-3.5 mr-1" />
                                  Tampilkan Rupiah
                                </>
                              )}
                            </Button>
                          </div>
                        )}
                      </div>
                      <CardTitle className="text-3xl font-bold tracking-tight text-emerald-400 mt-2 hover:scale-105 transition-transform origin-left">
                        {showRupiah
                          ? formatRupiah(
                              Math.max(0, totalSummary.remunBersih70),
                            )
                          : "Rp ***"}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="text-[13px] text-slate-300 space-y-3 pb-6 relative z-10">
                      <div className="space-y-2">
                        <div className="flex justify-between items-center text-emerald-300">
                          <span>Remun Kotor 100%</span>
                          <span className="font-bold">
                            {showRupiah
                              ? formatRupiah(
                                  Math.max(0, totalSummary.remunKotor100),
                                )
                              : "Rp ***"}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span>Rupiah Serdos</span>
                          <span className="font-semibold text-rose-400">
                            {showRupiah
                              ? formatRupiah(
                                  Math.max(0, totalSummary.rupiahSerdos),
                                )
                              : "Rp ***"}
                          </span>
                        </div>
                        {/* <div className="flex justify-between items-start border-b border-slate-700/50 pb-3">
                        <span>PPH</span>
                        <div className="text-right">
                          <div className="font-medium">
                            X ({(totalSummary.pphPersen * 100).toFixed(0)}%)
                          </div>
                          <div className="text-rose-400 text-xs">
                            {showRupiah
                              ? formatRupiah(
                                  Math.max(0, totalSummary.pphNominal),
                                )
                              : "Rp ***"}
                          </div>
                        </div>
                      </div> */}
                      </div>

                      <div className="bg-slate-800/80 p-3 rounded-lg border border-slate-700 mt-2">
                        <div className="font-bold text-white mb-2">
                          Remun 30%
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-slate-400">
                            Realisasi Kotor
                          </span>
                          <span className="font-bold text-indigo-300">
                            {showRupiah
                              ? formatRupiah(
                                  Math.max(0, totalSummary.remunKotor30),
                                )
                              : "Rp ***"}
                          </span>
                        </div>
                      </div>

                      <div className="bg-slate-800/80 p-3 rounded-lg border border-slate-700">
                        <div className="font-bold text-white mb-2">
                          Remun 70%
                        </div>
                        <div className="space-y-1.5">
                          <div className="flex justify-between items-center">
                            <span className="text-slate-400">Kotor</span>
                            <span className="font-bold text-blue-400">
                              {showRupiah
                                ? formatRupiah(
                                    Math.max(0, totalSummary.remunKotor70),
                                  )
                                : "Rp ***"}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="font-bold text-white">Bersih</span>
                            <span className="font-black text-indigo-400 text-sm">
                              {showRupiah
                                ? formatRupiah(
                                    Math.max(0, totalSummary.remunBersih70),
                                  )
                                : "Rp ***"}
                            </span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </ProtectRole>
              </div>
            </>
          </ProtectRole>
          <ProtectRole
            allowedRoles={[
              ROLES.ADMIN_SUPER,
              ROLES.ADMIN_UNIV,
              ROLES.ADMIN_KEPEGAWAIAN,
              ROLES.ADMIN_HTL,
              ROLES.ADMIN_FAKULTAS,
              ROLES.DOSEN,
            ]}
          >
            <>
              <Separator className="bg-slate-200" />
              <RincianPerBulan
                mappedMonths={mappedMonths}
                showRupiah={showRupiah}
                showNilai={showNilai}
                niu={niu as string}
                year={year}
                semester={semester}
                role={user?.role}
                totalPointKinerja={totalPointKinerja}
                pir={pir}
                isAdmin={isAdmin}
                isAlreadyConfirmed={isAlreadyConfirmed}
                isLolosSyaratMutlak={isLolosSyaratMutlak}
                periodeId={periodeId as string | number}
              />
              {/* <AutomatedIkuClaimer
                performanceData={performanceData}
                isBKDFulfilled={isBKDFulfilled}
                niu={niu}
                year={year}
                semester={semester}
                periodeId={periodeId}
                month={activeMonthFilterTemp}
                jabatanFungsional={currentMonthData?.jabatan_fungsional}
                queryClient={queryClient}
              /> */}
              <Separator className="bg-slate-200" />
            </>
          </ProtectRole>
          {/* Rincian Kinerja / Peforma */}
          <div className="space-y-4 pb-12">
            <ProtectRole
              allowedRoles={[
                ROLES.ADMIN_SUPER,
                ROLES.ADMIN_UNIV,
                ROLES.ADMIN_KEPEGAWAIAN,
                ROLES.ADMIN_HTL,
                ROLES.ADMIN_FAKULTAS,
              ]}
            >
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-bold tracking-tight text-slate-800">
                    Performa Kinerja
                  </h2>
                  <Badge
                    variant="secondary"
                    className="rounded-full bg-secondary/80 text-primary border-none"
                  >
                    {performanceData.length} Kegiatan
                  </Badge>
                </div>
              </div>
            </ProtectRole>

            {/* Highlight Total Point Kinerja & Struktural */}
            <ProtectRole
              allowedRoles={[
                ROLES.ADMIN_SUPER,
                ROLES.ADMIN_UNIV,
                ROLES.ADMIN_KEPEGAWAIAN,
                ROLES.ADMIN_HTL,
                ROLES.ADMIN_FAKULTAS,
                ...(showNilai ? [ROLES.DOSEN] : []),
              ]}
            >
              {/* Month Filter Tabs */}
              <div className="flex justify-center mb-8 mt-4 w-full px-4">
                <div className="overflow-x-auto scrollbar-hide pb-2 max-w-full">
                  <div className="flex items-center h-auto sm:h-14 p-1.5 bg-slate-100/80 backdrop-blur-md border border-slate-200/60 rounded-xl shadow-inner gap-1 min-w-max mx-auto">
                    {mappedMonths.map((m) => {
                      const isSelected = activeMonthFilter === m.month;
                      const [y, mm] = m.month.split("_");
                      const monthStr = new Date(
                        parseInt(y),
                        parseInt(mm) - 1,
                        1,
                      ).toLocaleString("id-ID", { month: "short" });

                      return (
                        <button
                          key={m.month}
                          onClick={() => setSelectedMonthFilter(m.month)}
                          className={`rounded-lg px-6 py-2.5 sm:py-0 h-full text-sm font-bold transition-all whitespace-nowrap flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-100 ${
                            isSelected
                              ? "bg-white text-indigo-700 shadow-sm"
                              : "text-slate-500 hover:text-slate-700 hover:bg-slate-50/50"
                          }`}
                        >
                          {`${monthStr}' ${y}`}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div className="bg-white border border-slate-200 text-slate-800 p-5 sm:p-6 rounded-xl text-sm shadow-lg mb-5 mx-auto w-full relative overflow-hidden">
                <div className="absolute top-0 right-0 p-4 opacity-[0.03] pointer-events-none">
                  <Target className="w-48 h-48 text-slate-900" />
                </div>
                <div className="relative z-10">
                  <div className="mb-6 flex flex-col xl:flex-row xl:items-center justify-between border-b border-slate-200 pb-4 gap-4">
                    <div className="flex flex-col gap-1.5">
                      <span className="text-slate-600 font-bold uppercase tracking-wider text-sm">
                        Capaian Kinerja Dasar :
                      </span>
                      {!isLolosSyaratMutlak && (
                        <div className="text-red-600 text-xs font-semibold bg-red-50 px-2.5 py-1.5 rounded-md border border-red-200 w-fit">
                          Belum lolos syarat mutlak. Kurang kriteria:{" "}
                          {[
                            !hasPenelitian && "Penelitian",
                            !hasPengabdian && "Pengabdian",
                            !hasPendidikan && "Pendidikan (Min 4 SKS)",
                            !hasPenunjang && "Penunjang",
                          ]
                            .filter(Boolean)
                            .join(", ")}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-4">
                      <ProtectRole
                        allowedRoles={[ROLES.ADMIN_SUPER, ROLES.ADMIN_UNIV]}
                      >
                        {maxSks !== null && (
                          <div
                            className="flex items-center"
                            title="Progress SKS Terpenuhi"
                          >
                            <ChartContainer
                              config={sksChartConfig}
                              className="w-14 h-14"
                            >
                              <RadialBarChart
                                data={[
                                  {
                                    name: "sks",
                                    value: showNilai
                                      ? Math.min(
                                          100,
                                          (sksDiakui / (maxSks || 1)) * 100,
                                        )
                                      : 0,
                                    fill:
                                      sksDiakui >= maxSks
                                        ? "#10b981"
                                        : "#6366f1",
                                  },
                                ]}
                                innerRadius="65%"
                                outerRadius="100%"
                                startAngle={90}
                                endAngle={-270}
                              >
                                <PolarGrid
                                  gridType="circle"
                                  radialLines={false}
                                  stroke="none"
                                  className="first:fill-slate-100 last:fill-white"
                                  polarRadius={[28, 18]}
                                />
                                <PolarAngleAxis
                                  type="number"
                                  domain={[0, 100]}
                                  angleAxisId={0}
                                  tick={false}
                                />
                                <RadialBar
                                  dataKey="value"
                                  cornerRadius={10}
                                  background={false}
                                />
                                <PolarRadiusAxis
                                  tick={false}
                                  tickLine={false}
                                  axisLine={false}
                                >
                                  <RechartsLabel
                                    content={({ viewBox }) => {
                                      if (
                                        viewBox &&
                                        "cx" in viewBox &&
                                        "cy" in viewBox
                                      ) {
                                        return (
                                          <text
                                            x={viewBox.cx}
                                            y={viewBox.cy}
                                            textAnchor="middle"
                                            dominantBaseline="middle"
                                          >
                                            <tspan
                                              x={viewBox.cx}
                                              y={viewBox.cy}
                                              className="fill-slate-700 text-xs font-bold"
                                            >
                                              {showNilai
                                                ? `${Math.round(
                                                    Math.min(
                                                      100,
                                                      (sksDiakui /
                                                        (maxSks || 1)) *
                                                        100,
                                                    ),
                                                  )}%`
                                                : "***"}
                                            </tspan>
                                          </text>
                                        );
                                      }
                                    }}
                                  />
                                </PolarRadiusAxis>
                              </RadialBarChart>
                            </ChartContainer>
                            <div className="flex flex-col ml-3">
                              <span className="text-slate-500 font-bold text-[9px] uppercase tracking-wider">
                                SKS Terpenuhi
                              </span>
                              <span className="text-slate-800 font-bold text-sm">
                                {showNilai
                                  ? `${roundNumber(sksDiakui, 2)} / ${maxSks}`
                                  : "***"}
                              </span>
                            </div>
                          </div>
                        )}
                      </ProtectRole>

                      {/* Status Penelitian & Pengabdian */}
                      <div className="flex gap-2 sm:ml-2 sm:border-l sm:border-slate-200 sm:pl-4 pl-0">
                        <Dialog>
                          <DialogTrigger asChild>
                            <button
                              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] sm:text-xs font-bold border transition-colors hover:shadow-sm ${
                                hasPenelitian
                                  ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
                                  : "bg-red-50 text-red-700 border-red-200 hover:bg-red-100"
                              }`}
                              title="Status Syarat Penelitian"
                            >
                              {hasPenelitian ? (
                                <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                              ) : (
                                <AlertTriangle className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                              )}
                              Penelitian
                            </button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>
                                Detail Syarat Penelitian
                              </DialogTitle>
                              <DialogDescription>
                                {hasPenelitian
                                  ? "Syarat penelitian telah terpenuhi untuk periode ini."
                                  : "Belum memenuhi syarat penelitian. Dibutuhkan minimal 1 kegiatan penelitian yang valid untuk memenuhi syarat mutlak kinerja."}
                              </DialogDescription>
                            </DialogHeader>
                            {data?.details_penelitian &&
                              data.details_penelitian.length > 0 && (
                                <div className="mt-4 text-sm space-y-2 max-h-60 overflow-y-auto">
                                  {data.details_penelitian.map(
                                    (p: any, i: number) => (
                                      <div
                                        key={i}
                                        className="p-3 bg-slate-50 border border-slate-200 rounded-md"
                                      >
                                        {p.kegiatan ||
                                          p.judul ||
                                          "Detail Kegiatan Penelitian"}
                                      </div>
                                    ),
                                  )}
                                </div>
                              )}
                          </DialogContent>
                        </Dialog>

                        <Dialog>
                          <DialogTrigger asChild>
                            <button
                              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] sm:text-xs font-bold border transition-colors hover:shadow-sm ${
                                hasPengabdian
                                  ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
                                  : "bg-red-50 text-red-700 border-red-200 hover:bg-red-100"
                              }`}
                              title="Status Syarat Pengabdian"
                            >
                              {hasPengabdian ? (
                                <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                              ) : (
                                <AlertTriangle className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                              )}
                              Pengabdian
                            </button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>
                                Detail Syarat Pengabdian
                              </DialogTitle>
                              <DialogDescription>
                                {hasPengabdian
                                  ? "Syarat pengabdian telah terpenuhi untuk periode ini."
                                  : "Belum memenuhi syarat pengabdian. Dibutuhkan minimal 1 kegiatan pengabdian yang valid untuk memenuhi syarat mutlak kinerja."}
                              </DialogDescription>
                            </DialogHeader>
                            {data?.details_pengabdian &&
                              data.details_pengabdian.length > 0 && (
                                <div className="mt-4 text-sm space-y-2 max-h-60 overflow-y-auto">
                                  {data.details_pengabdian.map(
                                    (p: any, i: number) => (
                                      <div
                                        key={i}
                                        className="p-3 bg-slate-50 border border-slate-200 rounded-md"
                                      >
                                        {p.kegiatan ||
                                          p.judul ||
                                          "Detail Kegiatan Pengabdian"}
                                      </div>
                                    ),
                                  )}
                                </div>
                              )}
                          </DialogContent>
                        </Dialog>
                      </div>
                    </div>
                  </div>

                  <div className="mb-3 text-slate-600 font-semibold text-xs tracking-wider uppercase">
                    Total Poin Capaian Kinerja Dasar :
                  </div>
                  <div className="border border-slate-200 rounded-lg overflow-hidden mb-8 shadow-sm">
                    <table className="w-full text-left border-collapse text-xs sm:text-sm">
                      <thead>
                        <tr className="border-b border-slate-200 bg-slate-50 text-slate-700 uppercase tracking-wider">
                          <th className="p-3 sm:p-4 font-semibold">Komponen</th>
                          <ProtectRole
                            allowedRoles={[
                              ROLES.ADMIN_SUPER,
                              ROLES.ADMIN_UNIV,
                              ROLES.ADMIN_KEPEGAWAIAN,
                              ROLES.ADMIN_HTL,
                              ROLES.ADMIN_FAKULTAS,
                              ROLES.DOSEN,
                            ]}
                          >
                            <th className="p-3 sm:p-4 font-semibold border-l border-slate-200 text-right">
                              Capaian
                            </th>
                            <th className="p-3 sm:p-4 font-semibold border-l border-slate-200 text-right">
                              Point Maksimal
                            </th>
                          </ProtectRole>
                          <th className="p-3 sm:p-4 font-semibold border-l border-slate-200 text-right text-emerald-700">
                            Point Terhitung
                          </th>
                        </tr>
                      </thead>
                      <tbody className="text-slate-700 divide-y divide-slate-100 bg-white">
                        <tr className="hover:bg-slate-50/80 transition-colors">
                          <td className="p-3 sm:p-4">
                            Pendidikan, Penelitian, Pengabdian
                          </td>
                          <ProtectRole
                            allowedRoles={[
                              ROLES.ADMIN_SUPER,
                              ROLES.ADMIN_UNIV,
                              ROLES.ADMIN_KEPEGAWAIAN,
                              ROLES.ADMIN_HTL,
                              ROLES.ADMIN_FAKULTAS,
                              ROLES.DOSEN,
                            ]}
                          >
                            <td className="p-3 sm:p-4 border-l border-slate-200 text-right">
                              {renderPointTerhitung(
                                rawPoinLainnya,
                                maxPoinLainnya,
                                poinLainnyaDiakui,
                              )}
                            </td>
                            <td className="p-3 sm:p-4 border-l border-slate-200 text-right">
                              {showNilai
                                ? roundNumber(maxPoinLainnya, 2)
                                : "***"}
                            </td>
                          </ProtectRole>
                          <td className="p-3 sm:p-4 border-l border-slate-200 text-right text-emerald-700 font-medium">
                            {showNilai
                              ? roundNumber(poinLainnyaDiakui, 2)
                              : "***"}
                          </td>
                        </tr>
                        <tr className="hover:bg-slate-50/80 transition-colors">
                          <td className="p-3 sm:p-4">Unsur Penunjang</td>
                          <ProtectRole
                            allowedRoles={[
                              ROLES.ADMIN_SUPER,
                              ROLES.ADMIN_UNIV,
                              ROLES.ADMIN_KEPEGAWAIAN,
                              ROLES.ADMIN_HTL,
                              ROLES.ADMIN_FAKULTAS,
                              ROLES.DOSEN,
                            ]}
                          >
                            <td className="p-3 sm:p-4 border-l border-slate-200 text-right">
                              {renderPointTerhitung(
                                rawPoinPenunjang,
                                maxPoinPenunjang,
                                poinPenunjangDiakui,
                              )}
                            </td>
                            <td className="p-3 sm:p-4 border-l border-slate-200 text-right">
                              {showNilai
                                ? roundNumber(maxPoinPenunjang, 2)
                                : "***"}
                            </td>
                          </ProtectRole>
                          <td className="p-3 sm:p-4 border-l border-slate-200 text-right text-emerald-700 font-medium">
                            {showNilai
                              ? roundNumber(poinPenunjangDiakui, 2)
                              : "***"}
                          </td>
                        </tr>
                        <tr className="bg-slate-50 font-bold text-slate-900 border-t-2 border-slate-200">
                          <td className="p-3 sm:p-4">Total</td>
                          <ProtectRole
                            allowedRoles={[
                              ROLES.ADMIN_SUPER,
                              ROLES.ADMIN_UNIV,
                              ROLES.ADMIN_KEPEGAWAIAN,
                              ROLES.ADMIN_HTL,
                              ROLES.ADMIN_FAKULTAS,
                              ROLES.DOSEN,
                            ]}
                          >
                            <td className="p-3 sm:p-4 border-l border-slate-200 text-right text-slate-400"></td>
                            <td className="p-3 sm:p-4 border-l border-slate-200 text-right text-slate-400"></td>
                          </ProtectRole>
                          <td className="p-3 sm:p-4 border-l border-slate-200 text-right text-emerald-700 text-base">
                            {showNilai
                              ? roundNumber(poinKinerjaDasar, 2)
                              : "***"}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="flex justify-start sm:justify-end mt-4">
                    <div className="space-y-3 w-full sm:w-80">
                      <div className="flex justify-between items-center text-slate-600">
                        <span className="font-medium text-xs sm:text-sm">
                          Total Poin IKU:
                        </span>
                        <span className="font-bold text-slate-900 text-sm sm:text-base">
                          {showNilai
                            ? roundNumber(finalTotalPointDiperoleh, 2)
                            : "***"}
                        </span>
                      </div>
                      <div className="flex justify-between items-center text-slate-600">
                        <span className="font-medium text-xs sm:text-sm">
                          Total Poin Struktural:
                        </span>
                        <span className="font-bold text-slate-900 text-sm sm:text-base">
                          {showNilai ? roundNumber(poinStruktural, 2) : "***"}
                        </span>
                      </div>
                      <div className="flex justify-between items-center text-slate-600">
                        <span className="font-medium text-xs sm:text-sm">
                          Total poin Remunerasi:
                        </span>
                        <span className="font-bold text-slate-900 text-sm sm:text-base">
                          {showNilai
                            ? roundNumber(totalPoinRemunerasi70, 2)
                            : "***"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </ProtectRole>

            {/* Tombol Tarik Data Evidence */}
            {/* <div className="flex items-center gap-2">
            <Button
              variant="outline"
              className="bg-white text-primary border-slate-200 hover:bg-slate-50 transition-colors gap-2 shadow-sm font-semibold rounded-lg"
              onClick={() => setOpenSyncConfirm(true)}
              disabled={syncMutation.isPending}
            >
              {syncMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin text-primary" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              {syncMutation.isPending
                ? "Menarik Data..."
                : "Tarik Data Evidence"}
            </Button>
          </div> */}
          </div>
        </div>

        <ProtectRole allowedRoles={[ROLES.DOSEN]}>
          <div className="w-full mt-6 grid grid-cols-1 md:grid-cols-2 gap-4 mb-2">
            <Dialog>
              <DialogTrigger asChild>
                <div
                  className={`flex items-center justify-between cursor-pointer p-4 rounded-xl border-2 transition-all hover:shadow-md ${hasPenelitian ? "border-emerald-200 bg-emerald-50/50 hover:bg-emerald-100/50" : "border-red-200 bg-red-50/50 hover:bg-red-100/50"}`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`p-2 rounded-full ${hasPenelitian ? "bg-emerald-100 text-emerald-600" : "bg-red-100 text-red-600"}`}
                    >
                      {hasPenelitian ? (
                        <CheckCircle2 className="w-5 h-5" />
                      ) : (
                        <AlertTriangle className="w-5 h-5" />
                      )}
                    </div>
                    <div>
                      <h4
                        className={`font-bold text-sm ${hasPenelitian ? "text-emerald-800" : "text-red-800"}`}
                      >
                        Status Syarat Penelitian
                      </h4>
                      <p
                        className={`text-xs ${hasPenelitian ? "text-emerald-600" : "text-red-600"}`}
                      >
                        {hasPenelitian
                          ? "Telah Terpenuhi (Klik detail)"
                          : "Belum Terpenuhi (Syarat Mutlak)"}
                      </p>
                    </div>
                  </div>
                  {!hasPenelitian && (
                    <span className="relative flex h-3 w-3 mr-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                    </span>
                  )}
                </div>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Detail Syarat Penelitian</DialogTitle>
                  <DialogDescription>
                    {hasPenelitian
                      ? "Syarat penelitian Anda telah terpenuhi untuk periode ini."
                      : "Anda belum memenuhi syarat penelitian. Dibutuhkan minimal 1 kegiatan penelitian yang valid untuk memenuhi syarat mutlak kinerja."}
                  </DialogDescription>
                </DialogHeader>
                {data?.details_penelitian &&
                  data.details_penelitian.length > 0 && (
                    <div className="mt-4 text-sm space-y-2 max-h-60 overflow-y-auto">
                      {data.details_penelitian.map((p: any, i: number) => (
                        <div
                          key={i}
                          className="p-3 bg-slate-50 border border-slate-200 rounded-md"
                        >
                          {p.kegiatan ||
                            p.judul ||
                            "Detail Kegiatan Penelitian"}
                        </div>
                      ))}
                    </div>
                  )}
              </DialogContent>
            </Dialog>

            <Dialog>
              <DialogTrigger asChild>
                <div
                  className={`flex items-center justify-between cursor-pointer p-4 rounded-xl border-2 transition-all hover:shadow-md ${hasPengabdian ? "border-emerald-200 bg-emerald-50/50 hover:bg-emerald-100/50" : "border-red-200 bg-red-50/50 hover:bg-red-100/50"}`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`p-2 rounded-full ${hasPengabdian ? "bg-emerald-100 text-emerald-600" : "bg-red-100 text-red-600"}`}
                    >
                      {hasPengabdian ? (
                        <CheckCircle2 className="w-5 h-5" />
                      ) : (
                        <AlertTriangle className="w-5 h-5" />
                      )}
                    </div>
                    <div>
                      <h4
                        className={`font-bold text-sm ${hasPengabdian ? "text-emerald-800" : "text-red-800"}`}
                      >
                        Status Syarat Pengabdian
                      </h4>
                      <p
                        className={`text-xs ${hasPengabdian ? "text-emerald-600" : "text-red-600"}`}
                      >
                        {hasPengabdian
                          ? "Telah Terpenuhi (Klik detail)"
                          : "Belum Terpenuhi (Syarat Mutlak)"}
                      </p>
                    </div>
                  </div>
                  {!hasPengabdian && (
                    <span className="relative flex h-3 w-3 mr-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                    </span>
                  )}
                </div>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Detail Syarat Pengabdian</DialogTitle>
                  <DialogDescription>
                    {hasPengabdian
                      ? "Syarat pengabdian Anda telah terpenuhi untuk periode ini."
                      : "Anda belum memenuhi syarat pengabdian. Dibutuhkan minimal 1 kegiatan pengabdian yang valid untuk memenuhi syarat mutlak kinerja."}
                  </DialogDescription>
                </DialogHeader>
                {data?.details_pengabdian &&
                  data.details_pengabdian.length > 0 && (
                    <div className="mt-4 text-sm space-y-2 max-h-60 overflow-y-auto">
                      {data.details_pengabdian.map((p: any, i: number) => (
                        <div
                          key={i}
                          className="p-3 bg-slate-50 border border-slate-200 rounded-md"
                        >
                          {p.kegiatan ||
                            p.judul ||
                            "Detail Kegiatan Pengabdian"}
                        </div>
                      ))}
                    </div>
                  )}
              </DialogContent>
            </Dialog>
          </div>
        </ProtectRole>

        <div className="w-full mt-8">
          <Tabs defaultValue="bkd" className="w-full">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
              <TabsList className="flex flex-col sm:flex-row h-auto sm:h-14 w-full sm:w-auto p-1.5 bg-slate-100/80 backdrop-blur-md border border-slate-200/60 rounded-xl shadow-inner gap-1">
                <TabsTrigger
                  value="bkd"
                  className="flex-1 sm:flex-none rounded-lg px-6 py-3 text-sm md:text-base font-bold data-[state=active]:bg-white data-[state=active]:text-indigo-700 data-[state=active]:shadow-md transition-all flex items-center justify-center gap-2.5 h-full text-slate-500 hover:text-slate-700 whitespace-nowrap ring-offset-slate-100 focus-visible:ring-2 focus-visible:ring-indigo-500"
                >
                  <Briefcase className="w-5 h-5 shrink-0" />
                  Kinerja Dasar (BKD)
                </TabsTrigger>
                <TabsTrigger
                  value="iku"
                  className="flex-1 sm:flex-none rounded-lg px-6 py-3 text-sm md:text-base font-bold data-[state=active]:bg-white data-[state=active]:text-indigo-700 data-[state=active]:shadow-md transition-all flex items-center justify-center gap-2.5 h-full text-slate-500 hover:text-slate-700 whitespace-nowrap ring-offset-slate-100 focus-visible:ring-2 focus-visible:ring-indigo-500"
                >
                  <Target className="w-5 h-5 shrink-0" />
                  Indikator Kinerja Utama (IKU)
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent
              value="bkd"
              className="mt-2 focus-visible:outline-none"
            >
              <div className="w-full">
                <ProtectRole
                  allowedRoles={[
                    ROLES.ADMIN_SUPER,
                    ROLES.ADMIN_UNIV,
                    ROLES.ADMIN_FAKULTAS,
                    ROLES.ADMIN_HTL,
                    ROLES.ADMIN_KEPEGAWAIAN,
                    ...(showNilai ? [ROLES.DOSEN] : []),
                  ]}
                >
                  <div className="flex justify-end mb-6 items-center gap-3">
                    <Label
                      htmlFor="rincian-switch"
                      className="font-semibold text-slate-700 cursor-pointer"
                    >
                      Tampilkan Rincian Perhitungan
                    </Label>
                    <Switch
                      id="rincian-switch"
                      checked={showRincian}
                      onCheckedChange={setShowRincian}
                    />
                  </div>
                </ProtectRole>

                {!showRincian ? (
                  <TabelValidasiKinerja
                    groupedPerformanceData={groupedPerformanceData}
                    performanceData={performanceData}
                    year={year}
                    semester={semester}
                    data={data}
                    setSelectedAgendaParams={setSelectedAgendaParams}
                    setAgendaSheetOpen={setAgendaSheetOpen}
                    periodeId={periodeId}
                    month={activeMonthFilterTemp}
                    showNilai={showNilai}
                    showValidasi={
                      showValidasi && isDosen
                        ? !isAlreadyConfirmed || invalidKinerjaItems.length > 0
                        : showValidasi
                    }
                    invalidKinerjaItems={invalidKinerjaItems}
                    isAlreadyConfirmed={isAlreadyConfirmed}
                  />
                ) : (
                  <TabelPeformaKinerja
                    groupedPerformanceData={groupedPerformanceData}
                    performanceData={performanceData}
                    year={year}
                    semester={semester}
                    data={data}
                    setSelectedAgendaParams={setSelectedAgendaParams}
                    setAgendaSheetOpen={setAgendaSheetOpen}
                    periodeId={periodeId}
                    month={activeMonthFilterTemp}
                    showNilai={showNilai}
                    showValidasi={showValidasi}
                  />
                )}
              </div>
            </TabsContent>

            <TabsContent
              value="iku"
              className="mt-2 focus-visible:outline-none"
            >
              <TabelIku
                jabatanFungsional={currentMonthData?.jabatan_fungsional}
                niu={niu as string}
                year={year}
                semester={semester}
                periodeId={periodeId}
                month={activeMonthFilterTemp}
                showNilai={showNilai}
                unitHomebase={data?.unit_homebase}
              />
            </TabsContent>
          </Tabs>
        </div>
        <AgendaSheet
          agendaSheetOpen={agendaSheetOpen}
          setAgendaSheetOpen={setAgendaSheetOpen}
          isAgendaLoading={isAgendaLoading}
          agendaData={agendaData}
          selectedAgendaParams={selectedAgendaParams}
        />

        {/* Floating Menu */}
        <div className="fixed bottom-6 right-0 z-50">
          {isMenuOpen && (
            <div className="absolute bottom-16 right-0 w-64 bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col mb-2 animate-in fade-in slide-in-from-bottom-4">
              <div className="bg-slate-50 border-b border-slate-100 p-3">
                <h3 className="font-bold text-slate-700 text-sm">
                  Pindah ke Unsur
                </h3>
              </div>
              <div className="max-h-[60vh] overflow-y-auto p-2 flex flex-col gap-1">
                {groupedPerformanceData.map(
                  (group, gIdx) =>
                    group.items.length > 0 && (
                      <button
                        key={gIdx}
                        onClick={() => scrollToUnsur(gIdx)}
                        className="text-left px-3 py-2 text-xs font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900 rounded-md transition-colors"
                      >
                        {group.unsur.nama_unsur}
                      </button>
                    ),
                )}
              </div>
            </div>
          )}
          {/* <Button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="w-14 h-14 rounded-full shadow-2xl bg-primary hover:bg-primary/90 flex items-center justify-center transition-transform hover:scale-105 p-0"
        >
          {isMenuOpen ? (
            <X className="w-6 h-6 text-white" />
          ) : (
            <Menu className="w-6 h-6 text-white" />
          )}
        </Button> */}
        </div>

        {/* Dialog Konfirmasi Penarikan Data Evidence */}
        <Dialog open={openSyncConfirm} onOpenChange={setOpenSyncConfirm}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-rose-600">
                <AlertTriangle className="w-5 h-5" />
                Konfirmasi Penarikan Data Evidence
              </DialogTitle>
              <DialogDescription className="pt-2 text-slate-600">
                Apakah Anda yakin ingin memulai penarikan data evidence dosen?
                Proses ini mungkin memerlukan waktu beberapa saat untuk
                menyelesaikan pengambilan data terbaru.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:justify-end mt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpenSyncConfirm(false)}
                disabled={syncMutation.isPending}
              >
                Batal
              </Button>
              <Button
                type="button"
                onClick={() => syncMutation.mutate()}
                disabled={syncMutation.isPending}
                className="gap-2 bg-primary hover:bg-primary/90"
              >
                {syncMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Memproses...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4" />
                    Ya, Tarik Data
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog Loading Penarikan Evidence */}
        <Dialog open={openSyncLoading} onOpenChange={setOpenSyncLoading}>
          <DialogContent className="sm:max-w-sm flex flex-col items-center justify-center p-8 [&>button.absolute]:hidden">
            <DialogHeader className="w-full text-center space-y-3">
              <DialogTitle className="text-center text-xl font-bold text-slate-800">
                Menarik Data Evidence...
              </DialogTitle>
              <DialogDescription className="text-center text-slate-500">
                Mohon tunggu sebentar, data sedang diproses. Anda bisa menutup
                popup ini dan proses akan tetap berjalan di latar belakang.
              </DialogDescription>
            </DialogHeader>

            <div className="w-56 h-56 -my-4 relative">
              <DotLottieReact
                src={coffeeLottieUrl}
                loop
                autoplay
                className="absolute inset-0 w-full h-full"
              />
            </div>

            <Button
              variant="outline"
              className="w-full mt-4 border-slate-200 text-slate-600 hover:bg-slate-50"
              onClick={() => setOpenSyncLoading(false)}
            >
              Tutup & Lanjutkan di Latar
            </Button>
          </DialogContent>
        </Dialog>
        {/* Dialog Detail Penelitian */}
        <Dialog open={openPenelitian} onOpenChange={setOpenPenelitian}>
          <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Detail Penelitian</DialogTitle>
              <DialogDescription>
                Daftar penelitian yang tercatat pada periode ini.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {(data?.details_penelitian?.length ?? 0) > 0 ? (
                <div className="space-y-3">
                  {(data?.details_penelitian || []).map(
                    (item: any, idx: number) => (
                      <div
                        key={item.id || idx}
                        className="p-4 border rounded-lg bg-slate-50"
                      >
                        <div className="font-semibold text-slate-800">
                          {item.judul}
                        </div>
                        <div className="text-sm text-slate-500 mt-1">
                          Jenis: {item.jenis}
                        </div>
                        <div className="text-sm text-slate-500">
                          Semester: {item.id_semester}
                        </div>
                      </div>
                    ),
                  )}
                </div>
              ) : (
                <div className="text-center text-slate-500 py-4">
                  Tidak ada data penelitian.
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Dialog Detail Pengabdian */}
        <Dialog open={openPengabdian} onOpenChange={setOpenPengabdian}>
          <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Detail Pengabdian</DialogTitle>
              <DialogDescription>
                Daftar pengabdian yang tercatat pada periode ini.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {(data?.details_pengabdian?.length ?? 0) > 0 ||
              (data?.detail_pengabdian?.length ?? 0) > 0 ? (
                <div className="space-y-3">
                  {(
                    data?.details_pengabdian ||
                    data?.detail_pengabdian ||
                    []
                  ).map((item: any, idx: number) => (
                    <div
                      key={item.id || idx}
                      className="p-4 border rounded-lg bg-slate-50"
                    >
                      <div className="font-semibold text-slate-800">
                        {item.judul}
                      </div>
                      <div className="text-sm text-slate-500 mt-1">
                        Jenis: {item.jenis}
                      </div>
                      <div className="text-sm text-slate-500">
                        Semester: {item.id_semester}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-slate-500 py-4">
                  Tidak ada data pengabdian.
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        <ProtectRole allowedRoles={[ROLES.DOSEN]}>
          <Dialog open={openPesan} onOpenChange={setOpenPesan}>
            <div className="fixed bottom-6 right-6 z-50 flex items-center justify-center w-14 h-14 group">
              <span className="absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75 animate-ping group-hover:hidden"></span>
              <DialogTrigger asChild>
                <Button
                  className="relative rounded-full w-14 h-14 shadow-lg flex items-center justify-center p-0 bg-primary hover:bg-primary/90 text-white border-2 border-white transition-transform hover:scale-110"
                  title="Ajukan Pertanyaan/Masukan"
                >
                  <MessageSquare className="w-6 h-6" />
                </Button>
              </DialogTrigger>
            </div>
            <AjukanPertanyaanForm setOpenPesan={setOpenPesan} />
          </Dialog>
        </ProtectRole>

        <RiwayatPertanyaanDialog
          open={openRiwayatPesan}
          onOpenChange={setOpenRiwayatPesan}
        />

        <ProtectRole allowedRoles={[]}>
          <div className="fixed bottom-6 right-6 z-50 flex items-center justify-center w-14 h-14">
            <span className="absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75 animate-ping"></span>
            <Button
              onClick={() => handleTimelineOpenChange(true)}
              className="relative rounded-full w-14 h-14 shadow-lg flex items-center justify-center p-0 bg-emerald-500 hover:bg-emerald-600 text-white border-2 border-white"
            >
              <Calendar className="w-6 h-6" />
            </Button>
          </div>
          <Dialog open={isTimelineOpen} onOpenChange={handleTimelineOpenChange}>
            <DialogContent className="sm:max-w-md md:max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-xl font-bold text-slate-800">
                  <Calendar className="w-6 h-6 text-primary" />
                  Jadwal Remunerasi 2026
                </DialogTitle>
              </DialogHeader>
              <div className="relative border-l-2 border-slate-200 ml-4 md:ml-6 space-y-8 mt-4">
                {[
                  {
                    step: 1,
                    title:
                      "Verifikasi data kinerja oleh Tim Verifikasi Kinerja",
                    date: "29 Juni–5 Juli 2026",
                  },
                  {
                    step: 2,
                    title:
                      "Validasi data kinerja oleh dosen pada Sistem Remunerasi",
                    date: "6–9 Juli 2026",
                  },
                  {
                    step: 3,
                    title:
                      "Validasi kinerja dosen oleh Wakil Dekan II/Wakil Direktur II Pascasarjana pada Sistem Remunerasi",
                    date: "9–11 Juli 2026",
                  },
                  {
                    step: 4,
                    title:
                      "Validasi poin remunerasi oleh Tim Implementasi Remunerasi",
                    date: "11–17 Juli 2026",
                  },
                ].map((item, index) => (
                  <div key={index} className="relative pl-8 md:pl-10">
                    <span className="absolute -left-4.25 top-0 flex items-center justify-center w-8 h-8 bg-primary/10 border-4 border-white rounded-full text-sm font-bold text-primary shadow-sm">
                      {item.step}
                    </span>
                    <div className="bg-slate-50 border border-slate-100 p-5 rounded-xl hover:bg-slate-100/80 hover:border-slate-200 transition-all duration-300">
                      <h4 className="text-sm md:text-base font-semibold text-slate-800 leading-snug mb-2">
                        {item.title}
                      </h4>
                      <p className="text-sm font-medium text-primary flex items-center gap-1.5">
                        <Clock className="w-4 h-4" />
                        {item.date}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </DialogContent>
          </Dialog>
        </ProtectRole>

        <QnaRemunerasiDialog />
        <RemunInfoFooter />
      </div>
    </TooltipProvider>
  );
}
